import { memo, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Calendar,
  CheckCircle,
  Plus,
  AlertCircle,
  Eye,
  Shield,
  MapPin,
  Lock,
  Info,
  Banknote,
  Clock,
  Globe,
  ChevronDown,
  ChevronUp,
  FileText,
  Users,
  Edit2,
  Package,
  Check,
  Target,
  Car,
  Plane,
  Home,
  Heart,
  Flame,
  Ship,
  Scale,
  AlertTriangle,
} from "lucide-react";
import Images from "../utils/Images"
import { formatMoney } from "../utils/formatMoney";

// =========================
// ROUTE DATA - Cleverly stored to map to backend
// =========================
const REGIONAL_ROUTES = [
  { destination: "Volta Region", hours: 6, maxPrice: 10 },
  { destination: "Eastern Region", hours: 4, maxPrice: 10 },
  { destination: "Ashanti Region", hours: 8, maxPrice: 10 },
  { destination: "Central Region", hours: 6, maxPrice: 10 },
  { destination: "Western Region", hours: 8, maxPrice: 10 },
  { destination: "Upper West Region", hours: 20, maxPrice: 10 },
];

const INTERNATIONAL_ROUTES = [
  { destination: "Togo", hours: 8, maxPrice: 20 },
  { destination: "Benin", hours: 12, maxPrice: 20 },
  { destination: "Nigeria", hours: 24, maxPrice: 20 },
  { destination: "Ivory Coast", hours: 15, maxPrice: 20 },
  { destination: "Burkina Faso", hours: 20, maxPrice: 20 },
];

export const FIXED_COMMUTER_COVERAGES = [
  {
    key: "sevenDayPrice",
    label: "7 Days",
    maxPrice: 5,
    hours: 168,
    display_unit: "days",
    display_value: 7,
  },
  {
    key: "oneMonthPrice",
    label: "1 Month",
    maxPrice: 19,
    hours: 720,
    display_unit: "months",
    display_value: 1,
  },
];

export const isFixedDurationPolicy = (policyType) =>
  policyType === "okada_rider" || policyType === "daily_commuter";

export const parseFixedCoveragePrices = (apiDurations = []) => {
  const prices = { sevenDayPrice: "", oneMonthPrice: "" };
  if (!Array.isArray(apiDurations) || apiDurations.length === 0) return prices;

  const toInputValue = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    const rounded = Math.round((n + Number.EPSILON) * 100) / 100;
    return Number.isInteger(rounded) ? String(Math.trunc(rounded)) : rounded.toFixed(2);
  };

  apiDurations.forEach((duration) => {
    const unit = String(duration.display_unit || "").toLowerCase();
    const value = Number(duration.display_value);
    const hours = Number(duration.hours);
    const price = duration.price != null ? toInputValue(duration.price) : "";

    const isSevenDay = hours === 168 || (unit.startsWith("day") && value === 7);
    const isOneMonth =
      hours === 720 ||
      (unit.startsWith("month") && value === 1) ||
      (unit.startsWith("day") && value === 30);

    if (isSevenDay) prices.sevenDayPrice = price;
    else if (isOneMonth) prices.oneMonthPrice = price;
  });

  if (!prices.sevenDayPrice && !prices.oneMonthPrice && apiDurations[0]?.price != null) {
    prices.oneMonthPrice = toInputValue(apiDurations[0].price);
  }

  return prices;
};

export const buildFixedCoverageDurations = (formData) =>
  FIXED_COMMUTER_COVERAGES.map((tier) => ({
    hours: tier.hours,
    price: Math.round(((parseFloat(formData[tier.key]) || 0) + Number.EPSILON) * 100) / 100,
    display_unit: tier.display_unit,
    display_value: tier.display_value,
  }));

export const validateFixedCoveragePrices = (formData, errors = {}) => {
  FIXED_COMMUTER_COVERAGES.forEach((tier) => {
    const raw = formData[tier.key];
    const value = parseFloat(raw);
    if (!raw || Number.isNaN(value) || value < 0.01) {
      errors[tier.key] = `Please set a price of at least ₵0.01 for ${tier.label}`;
    } else if (value > tier.maxPrice) {
      errors[tier.key] = `${tier.label} price cannot exceed ₵${tier.maxPrice}`;
    }
  });
  return errors;
};

export const formatFixedDurationLabel = (duration) => {
  const unit = String(duration?.display_unit || "").toLowerCase();
  const value = Number(duration?.display_value);
  if (value && unit.startsWith("month")) return value === 1 ? "1 Month" : `${value} Months`;
  if (value && unit.startsWith("day")) return value === 1 ? "1 Day" : `${value} Days`;
  if (duration?.hours === 168) return "7 Days";
  if (duration?.hours === 720) return "1 Month";
  return duration?.hours ? `${duration.hours} Hours` : "Coverage";
};

// =========================
// Reusable Components
// =========================

// Route Selection Card for Intercity Travel
export const RouteSelectionCard = memo(function RouteSelectionCard({
  route,
  isSelected,
  price,
  onToggle,
  onPriceChange,
}) {
  // Real-time validation for price limits
  const isPriceExceeded = price && parseFloat(price) > route.maxPrice;
  const isPriceTooLow = price && parseFloat(price) < 0.01;

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      onClick={onToggle}
      className={`p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer self-start ${
        isSelected
          ? "bg-blue-50 border-blue-500 shadow-md"
          : "bg-white border-gray-200 hover:border-gray-300"
      }`}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <div className="flex items-center mb-2 space-x-2">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => e.stopPropagation()}
              className="w-5 h-5 text-blue-600 rounded border-gray-300 pointer-events-none focus:ring-blue-500"
            />
            <label className="font-semibold text-gray-900 cursor-pointer">
              Greater Accra → {route.destination}
            </label>
          </div>
          <div className="flex items-center ml-7 space-x-4 text-sm text-gray-600">
            <div className="flex items-center space-x-1">
              <Clock className="w-4 h-4" />
              <span>{route.hours} hrs</span>
            </div>
            <div className="flex items-center space-x-1">
              <Banknote className="w-4 h-4" />
              <span>Max: ₵{route.maxPrice}</span>
            </div>
          </div>
        </div>
      </div>

      {isSelected && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="pt-3 mt-3 ml-7 border-t border-gray-200"
          onClick={(e) => e.stopPropagation()} // Prevent card toggle when clicking in price section
        >
          <label className="block mb-2 text-xs font-semibold text-gray-600">
            Set Your Price (₵) <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 font-bold text-gray-500 transform -translate-y-1/2">
              ₵
            </span>
            <input
              type="number"
              min="0.01"
              max={route.maxPrice}
              step="0.01"
              value={price}
              onChange={(e) => onPriceChange(e.target.value)}
              placeholder={`Enter amount (max ${route.maxPrice})`}
              className={`py-2 pr-4 pl-8 w-full rounded-lg border transition-all duration-200 ${
                isPriceExceeded
                  ? "bg-red-50 border-red-500 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  : isPriceTooLow
                  ? "bg-yellow-50 border-yellow-500 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  : "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              }`}
            />
          </div>
          {/* Real-time validation notices */}
          {isPriceExceeded && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start mt-2 space-x-1"
            >
              <AlertCircle className="w-3 h-3 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs font-medium text-red-600">
                Price exceeds maximum! Regional routes cannot exceed ₵
                {route.maxPrice}.
              </p>
            </motion.div>
          )}

          {isPriceTooLow && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start mt-2 space-x-1"
            >
              <AlertCircle className="w-3 h-3 text-yellow-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs font-medium text-yellow-600">
                Price too low! Minimum amount is ₵0.01.
              </p>
            </motion.div>
          )}

          {!isPriceExceeded && !isPriceTooLow && (
            <div className="flex items-start mt-2 space-x-1">
              <Info className="w-3 h-3 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-gray-600">
                Set any amount from ₵0.01 up to ₵{route.maxPrice}. Lower prices
                may attract more customers.
              </p>
            </div>
          )}
        </motion.div>
      )}
    </motion.div>
  );
});

// Insurance Coverage/Benefit Item
export const InsuranceCoverageItem = memo(function InsuranceCoverageItem({
  index,
  item,
  onChangeField,
  onRemove,
  title = "Benefit",
}) {
  return (
    <motion.div
      key={`insurance-coverage-${item.id}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 bg-gray-50 rounded-lg border border-gray-200"
    >
      <div className="flex justify-between items-center mb-3">
        <span className="text-sm font-medium text-gray-700">
          {title} #{index + 1}
        </span>
        <button
          onClick={() => onRemove(item.id)}
          className="p-1 text-red-600 rounded-lg transition-colors hover:bg-red-50"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="block mb-1 text-xs font-medium text-gray-600">
            Benefit Title
          </label>
          <input
            type="text"
            value={item.title}
            onChange={(e) => onChangeField(item.id, "title", e.target.value)}
            placeholder="e.g., Medical Emergency"
            className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block mb-1 text-xs font-medium text-gray-600">
            Coverage Limit
          </label>
          <input
            type="text"
            value={item.limit}
            onChange={(e) => onChangeField(item.id, "limit", e.target.value)}
            placeholder="e.g., ₵50,000"
            className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
      <div className="mt-3">
        <label className="block mb-1 text-xs font-medium text-gray-600">
          Description
        </label>
        <textarea
          value={item.description}
          onChange={(e) =>
            onChangeField(item.id, "description", e.target.value)
          }
          placeholder="Describe what this benefit includes..."
          rows={2}
          className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </motion.div>
  );
});

// Package Drawer for editing packages
export const PackageDrawer = memo(function PackageDrawer({
  isOpen,
  onClose,
  package: pkg,
  index,
  onSave,
  insuranceType = "life",
}) {
  const [editedPackage, setEditedPackage] = useState(pkg || {});
  const [expandedSection, setExpandedSection] = useState("basic");

  useEffect(() => {
    if (pkg) {
      setEditedPackage(pkg);
    }
  }, [pkg]);

  const updateField = (field, value) => {
    setEditedPackage((prev) => ({ ...prev, [field]: value }));
  };

  // Helper functions for managing nested arrays
  const addBenefit = () => {
    const benefits = editedPackage.insurance_coverage_list || [];
    const newBenefit = {
      id: Date.now() + Math.random(),
      title: "",
      description: "",
      limit: "",
    };
    updateField("insurance_coverage_list", [...benefits, newBenefit]);
  };

  const updateBenefit = (benefitId, field, value) => {
    const benefits = editedPackage.insurance_coverage_list || [];
    const updated = benefits.map((b) =>
      b.id === benefitId ? { ...b, [field]: value } : b
    );
    updateField("insurance_coverage_list", updated);
  };

  const removeBenefit = (benefitId) => {
    const benefits = editedPackage.insurance_coverage_list || [];
    const filtered = benefits.filter((b) => b.id !== benefitId);
    updateField("insurance_coverage_list", filtered);
  };

  const addExclusion = () => {
    const exclusions = editedPackage.exclusions || [];
    const newExclusion = {
      id: Date.now() + Math.random(),
      text: "",
    };
    updateField("exclusions", [...exclusions, newExclusion]);
  };

  const updateExclusion = (exclusionId, value) => {
    const exclusions = editedPackage.exclusions || [];
    const updated = exclusions.map((e) =>
      e.id === exclusionId ? { ...e, text: value } : e
    );
    updateField("exclusions", updated);
  };

  const removeExclusion = (exclusionId) => {
    const exclusions = editedPackage.exclusions || [];
    const filtered = exclusions.filter((e) => e.id !== exclusionId);
    updateField("exclusions", filtered);
  };

  const addEligibility = () => {
    const criteria = editedPackage.eligibility_criteria || [];
    updateField("eligibility_criteria", [...criteria, ""]);
  };

  const updateEligibility = (idx, value) => {
    const criteria = editedPackage.eligibility_criteria || [];
    const updated = [...criteria];
    updated[idx] = value;
    updateField("eligibility_criteria", updated);
  };

  const removeEligibility = (idx) => {
    const criteria = editedPackage.eligibility_criteria || [];
    const filtered = criteria.filter((_, i) => i !== idx);
    updateField("eligibility_criteria", filtered);
  };

  const addClaimStep = () => {
    const steps = editedPackage.claim_process_steps || [];
    const newStep = {
      id: Date.now() + Math.random(),
      step_number: steps.length + 1,
      title: "",
      description: "",
      timeframe: "",
      requirements: "",
    };
    updateField("claim_process_steps", [...steps, newStep]);
  };

  const updateClaimStep = (stepId, field, value) => {
    const steps = editedPackage.claim_process_steps || [];
    const updated = steps.map((s) =>
      s.id === stepId ? { ...s, [field]: value } : s
    );
    updateField("claim_process_steps", updated);
  };

  const removeClaimStep = (stepId) => {
    const steps = editedPackage.claim_process_steps || [];
    const filtered = steps.filter((s) => s.id !== stepId);
    const renumbered = filtered.map((s, idx) => ({
      ...s,
      step_number: idx + 1,
    }));
    updateField("claim_process_steps", renumbered);
  };

  /** Generate a safe field key from label (e.g. "Vehicle Registration" → "vehicle_registration") */
  const labelToKey = (label) => {
    if (!label || !String(label).trim()) return "";
    const slug = String(label)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return slug || "";
  };

  const quoteFields = editedPackage.quote_request_fields || [];
  const addQuoteField = () => {
    const newField = {
      id: Date.now() + Math.random(),
      key: `field_${quoteFields.length + 1}`,
      label: "",
      type: "text",
      required: true,
      options: [], // for type "select"
    };
    updateField("quote_request_fields", [...quoteFields, newField]);
  };
  const updateQuoteField = (fieldId, field, value) => {
    const updated = quoteFields.map((f) => {
      if (f.id !== fieldId) return f;
      const next = { ...f, [field]: value };
      if (field === "label") {
        const generated = labelToKey(value);
        next.key = generated || next.key || `field_${fieldId}`;
      }
      return next;
    });
    updateField("quote_request_fields", updated);
  };
  const removeQuoteField = (fieldId) => {
    updateField(
      "quote_request_fields",
      quoteFields.filter((f) => f.id !== fieldId)
    );
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleSave = () => {
    // Validate required fields
    const errors = [];
    
    if (!editedPackage.productName?.trim()) {
      errors.push("Product Name is required");
    }
    if (!editedPackage.is_quote_only) {
      if (!editedPackage.premium || parseFloat(editedPackage.premium) <= 0) {
        errors.push("Premium must be greater than 0");
      }
    }
    if (!editedPackage.durationValue || parseInt(editedPackage.durationValue) <= 0) {
      errors.push("Duration value must be greater than 0");
    }
    if (!editedPackage.description?.trim()) {
      errors.push("Description is required");
    }
    const maxClaims = Number(editedPackage.max_claims);
    if (!Number.isInteger(maxClaims) || maxClaims < 1 || maxClaims > 999) {
      errors.push("Max Claims is required (1–999)");
    }
    if (editedPackage.is_quote_only) {
      const qf = editedPackage.quote_request_fields || [];
      if (!qf.length || qf.some((f) => !(f.label && f.label.trim()) || !(f.key && f.key.trim()))) {
        errors.push("Quote-Based packages must have at least one quote request field with key and label");
      }
    }

    const hasBenefits = (editedPackage.insurance_coverage_list || []).length > 0;
    const hasValidBenefits = (editedPackage.insurance_coverage_list || []).every(
      (b) => b.title?.trim() && b.description?.trim()
    );
    if (!hasBenefits) {
      errors.push("At least 1 Coverage Benefit is required");
    } else if (!hasValidBenefits) {
      errors.push("All benefits must have title and description");
    }
    
    const hasEligibility = (editedPackage.eligibility_criteria || []).filter(
      (c) => c && c.trim() !== ""
    ).length > 0;
    if (!hasEligibility) {
      errors.push("At least 1 Eligibility Criterion is required");
    }
    
    const hasClaimSteps = (editedPackage.claim_process_steps || []).length > 0;
    const hasValidClaimSteps = (editedPackage.claim_process_steps || []).every(
      (s) => s.title?.trim() && s.description?.trim() && s.timeframe?.trim()
    );
    if (!hasClaimSteps) {
      errors.push("At least 1 Claim Process Step is required");
    } else if (!hasValidClaimSteps) {
      errors.push("All claim steps must have title, description, and timeframe");
    }
    
    const hasTerms = editedPackage.terms && editedPackage.terms.trim() !== "";
    if (!hasTerms) {
      errors.push("Terms & Conditions is required");
    }
    
    if (errors.length > 0) {
      alert("Please fix the following errors:\n\n" + errors.join("\n"));
      return;
    }
    
    onSave(editedPackage);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex fixed inset-0 z-50 justify-end bg-black bg-opacity-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          onClick={(e) => e.stopPropagation()}
          className="overflow-y-auto w-full bg-white shadow-2xl sm:max-w-2xl"
        >
          {/* Header */}
          <div className="sticky top-0 z-10 px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 shadow-lg">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                  <Package className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {pkg?.id ? `Package #${index + 1}` : "Add New Package"}
                  </h2>
                  <p className="text-sm text-blue-100">
                    Configure package details and policy terms
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-white rounded-lg transition-colors hover:bg-white hover:bg-opacity-20"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* All Sections - Collapsible */}
            <div className="space-y-4">
              {/* Basic Information Section */}
              <div className="overflow-hidden bg-white rounded-lg border-2 border-blue-300 shadow-sm">
                <button
                  onClick={() => toggleSection("basic")}
                  className="flex justify-between items-center px-4 py-3 w-full text-left transition-colors hover:bg-blue-50"
                >
                  <div className="flex items-center space-x-2">
                    <Info className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-semibold text-gray-900">
                      Basic Information
                    </span>
                    <span className="px-2 py-0.5 text-xs font-medium text-red-600 bg-red-50 rounded-full">
                      Required
                    </span>
                  </div>
                  {expandedSection === "basic" ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                <AnimatePresence>
                  {expandedSection === "basic" && (
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: "auto" }}
                      exit={{ height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="p-4 space-y-4 bg-blue-50 border-t border-blue-200">
                        <div>
                          <label className="block mb-2 text-sm font-medium text-gray-700">
                            Product Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={editedPackage.productName || ""}
                            onChange={(e) => updateField("productName", e.target.value)}
                            placeholder="e.g., Premium Life Plan"
                            className="px-4 py-2.5 w-full text-sm bg-white rounded-lg border border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        {/* Step 1: Decide if price is fixed or quote-based + Quote fields directly below */}
                        <div className="p-4 rounded-lg border-2 border-amber-200 bg-amber-50 space-y-4">
                          <label className="flex gap-2 items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!editedPackage.is_quote_only}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                updateField("is_quote_only", checked);
                                if (checked) updateField("premium", "");
                              }}
                              className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                            />
                            <span className="text-sm font-semibold text-gray-800">Quote-Based Only</span>
                          </label>
                          <p className="text-xs text-amber-800">
                            Check this if you will provide the price when the customer requests a quote. You do not need to enter a premium here—it will be sent as 0 until you send a quote.
                          </p>
                          {editedPackage.is_quote_only && (
                            <div className="pt-3 border-t border-amber-300 space-y-3">
                              <p className="text-sm font-semibold text-amber-900">Quote request fields</p>
                              <p className="text-xs text-amber-800">Add the fields you want to collect when a customer requests a quote (e.g. vehicle reg, trip dates).</p>
                              {(editedPackage.quote_request_fields || []).length > 0 && (
                                <div className="space-y-3">
                                  {(editedPackage.quote_request_fields || []).map((qf) => (
                                    <div key={qf.id} className="p-3 bg-white rounded-lg border border-amber-300 space-y-2">
                                      <div className="flex justify-between items-center">
                                        <span className="text-xs font-medium text-amber-800">Field</span>
                                        <button type="button" onClick={() => removeQuoteField(qf.id)} className="p-1 text-red-600 hover:bg-red-100 rounded">
                                          <X className="w-4 h-4" />
                                        </button>
                                      </div>
                                      <div>
                                        <label className="block text-xs font-medium text-amber-800 mb-0.5">Label (shown to customer)</label>
                                        <input
                                          type="text"
                                          value={qf.label || ""}
                                          onChange={(e) => updateQuoteField(qf.id, "label", e.target.value)}
                                          placeholder="e.g. Vehicle Registration Number"
                                          className="px-2 py-1.5 w-full text-sm rounded border border-amber-300"
                                        />
                                        {(qf.key || qf.label) && (
                                          <p className="mt-1 text-xs text-amber-700">
                                            Field name: <code className="bg-amber-100 px-1 rounded">{qf.key || labelToKey(qf.label) || "—"}</code>
                                          </p>
                                        )}
                                      </div>
                                      <div className="flex gap-2 items-center flex-wrap">
                                        <select
                                          value={qf.type || "text"}
                                          onChange={(e) => updateQuoteField(qf.id, "type", e.target.value)}
                                          className="px-2 py-1.5 text-sm rounded border border-amber-300"
                                        >
                                          <option value="text">Text</option>
                                          <option value="number">Number</option>
                                          <option value="email">Email</option>
                                          <option value="textarea">Long text</option>
                                          <option value="date">Date</option>
                                          <option value="select">Dropdown</option>
                                        </select>
                                        <label className="flex gap-1 items-center text-xs">
                                          <input
                                            type="checkbox"
                                            checked={!!qf.required}
                                            onChange={(e) => updateQuoteField(qf.id, "required", e.target.checked)}
                                            className="rounded border-amber-300"
                                          />
                                          Required
                                        </label>
                                      </div>
                                      {(qf.type === "select") && (
                                        <input
                                          type="text"
                                          value={Array.isArray(qf.options) ? qf.options.join(", ") : (qf.options ?? "")}
                                          onChange={(e) => updateQuoteField(qf.id, "options", e.target.value)}
                                          placeholder="Options (comma-separated, e.g. Yes, No, Maybe)"
                                          className="px-2 py-1.5 w-full text-sm rounded border border-amber-300"
                                        />
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={addQuoteField}
                                className="flex items-center justify-center gap-2 w-full py-3 px-4 text-sm font-bold text-white bg-amber-600 rounded-xl shadow-md hover:bg-amber-700 focus:ring-4 focus:ring-amber-300 transition-colors"
                              >
                                <Plus className="w-5 h-5" strokeWidth={2.5} />
                                Add quote field
                              </button>
                            </div>
                          )}
                        </div>
                        {/* Step 2: Premium only when not quote-based */}
                        {!editedPackage.is_quote_only && (
                          <div className="space-y-4 sm:grid sm:grid-cols-2 sm:gap-4 sm:space-y-0">
                            <div>
                              <label className="block mb-2 text-sm font-medium text-gray-700">
                                Premium (₵) <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={editedPackage.premium || ""}
                                onChange={(e) => updateField("premium", e.target.value)}
                                placeholder="e.g., 150.00"
                                className="px-4 py-2.5 w-full text-sm bg-white rounded-lg border border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block mb-2 text-sm font-medium text-gray-700">
                                Contribution Type <span className="text-red-500">*</span>
                              </label>
                              <select
                                value={editedPackage.premiumDuration || "monthly"}
                                onChange={(e) => updateField("premiumDuration", e.target.value)}
                                className="px-4 py-2.5 w-full text-sm bg-white rounded-lg border border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                                <option value="yearly">Yearly</option>
                              </select>
                            </div>
                          </div>
                        )}
                        <div className="space-y-4 sm:grid sm:grid-cols-2 sm:gap-4 sm:space-y-0">
                          <div className="sm:col-span-2">
                            <label className="block mb-2 text-sm font-medium text-gray-700">
                              Package Duration <span className="text-red-500">*</span>
                            </label>
                            <div className="flex gap-2">
                              <input
                                type="number"
                                min="1"
                                step="1"
                                value={editedPackage.durationValue || ""}
                                onChange={(e) =>
                                  updateField("durationValue", e.target.value)
                                }
                                placeholder="Qty"
                                className="px-3 py-2.5 w-20 text-sm bg-white rounded-lg border border-blue-300 sm:w-24 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <select
                                value={
                                  editedPackage.durationUnit ||
                                  (insuranceType === "travel" ? "hours" : "years")
                                }
                                onChange={(e) =>
                                  updateField("durationUnit", e.target.value)
                                }
                                className="flex-1 px-2 py-2.5 text-sm bg-white rounded-lg border border-blue-300 sm:px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                {insuranceType === "travel" || insuranceType === "life" || insuranceType === "general" ? (
                                  <>
                                    <option value="hours">Hours</option>
                                    <option value="hour">Hour</option>
                                    <option value="days">Days</option>
                                    <option value="weeks">Weeks</option>
                                    <option value="months">Months</option>
                                    <option value="years">Years</option>
                                  </>
                                ) : (
                                  <>
                                    <option value="years">Years</option>
                                    <option value="months">Months</option>
                                  </>
                                )}
                              </select>
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className="block mb-2 text-sm font-medium text-gray-700">
                            Description <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            value={editedPackage.description || ""}
                            onChange={(e) => updateField("description", e.target.value)}
                            placeholder="e.g., Comprehensive coverage with extended benefits for individuals and families. Includes medical emergencies, hospitalization, and critical illness support."
                            rows={3}
                            className="px-4 py-2.5 w-full text-sm bg-white rounded-lg border border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block mb-2 text-sm font-medium text-gray-700">
                            Max Claims <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            min={1}
                            max={999}
                            value={editedPackage.max_claims ?? ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              updateField("max_claims", val === "" ? "" : Number(val));
                            }}
                            placeholder="e.g. 3"
                            className="px-4 py-2.5 w-full text-sm bg-white rounded-lg border border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Maximum number of claims allowed for this package (per coverage period).
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Benefits Section */}
              <div className="overflow-hidden bg-white rounded-lg border-2 border-gray-200 shadow-sm">
                <button
                  onClick={() => toggleSection("benefits")}
                  className="flex justify-between items-center px-4 py-3 w-full text-left transition-colors hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-2">
                    <Shield className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-semibold text-gray-900">
                      Coverage Benefits
                    </span>
                    <span className="px-2 py-0.5 text-xs font-medium text-red-600 bg-red-50 rounded-full">
                      Required
                    </span>
                    <span className="px-2 py-0.5 text-xs font-medium text-green-600 bg-green-50 rounded-full">
                      {(editedPackage.insurance_coverage_list || []).length}
                    </span>
                  </div>
                  {expandedSection === "benefits" ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                <AnimatePresence>
                  {expandedSection === "benefits" && (
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: "auto" }}
                      exit={{ height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="p-4 space-y-3 border-t border-gray-200">
                        {(editedPackage.insurance_coverage_list || []).map(
                          (benefit, idx) => (
                            <div
                              key={benefit.id}
                              className="p-3 bg-green-50 rounded-lg border border-green-200"
                            >
                              <div className="flex justify-between items-start mb-2">
                                <span className="text-xs font-medium text-green-700">
                                  Benefit #{idx + 1}
                                </span>
                                <button
                                  onClick={() => removeBenefit(benefit.id)}
                                  className="p-1 text-red-600 rounded transition-colors hover:bg-red-100"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="space-y-2">
                                <input
                                  type="text"
                                  value={benefit.title || ""}
                                  onChange={(e) =>
                                    updateBenefit(
                                      benefit.id,
                                      "title",
                                      e.target.value
                                    )
                                  }
                                  placeholder="e.g., Medical Emergency Coverage"
                                  className="px-3 py-2 w-full text-sm rounded-lg border border-green-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                                <textarea
                                  value={benefit.description || ""}
                                  onChange={(e) =>
                                    updateBenefit(
                                      benefit.id,
                                      "description",
                                      e.target.value
                                    )
                                  }
                                  placeholder="e.g., Coverage for emergency medical treatments, hospitalization, and ambulance services"
                                  rows={2}
                                  className="px-3 py-2 w-full text-sm rounded-lg border border-green-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={benefit.limit || ""}
                                  onChange={(e) =>
                                    updateBenefit(
                                      benefit.id,
                                      "limit",
                                      e.target.value
                                    )
                                  }
                                  placeholder="e.g., 50000 (Coverage limit amount we pay out)"
                                  className="px-3 py-2 w-full text-sm rounded-lg border border-green-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                              </div>
                            </div>
                          )
                        )}
                        <button
                          onClick={addBenefit}
                          className="flex items-center px-4 py-2 w-full text-sm font-medium text-green-700 bg-green-50 rounded-lg border border-green-200 transition-colors hover:bg-green-100"
                        >
                          <Plus className="mr-2 w-4 h-4" />
                          Add Benefit
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Exclusions Section */}
              <div className="overflow-hidden bg-white rounded-lg border-2 border-gray-200 shadow-sm">
                <button
                  onClick={() => toggleSection("exclusions")}
                  className="flex justify-between items-center px-4 py-3 w-full text-left transition-colors hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <span className="text-sm font-semibold text-gray-900">
                      Exclusions
                    </span>
                    <span className="px-2 py-0.5 text-xs font-medium text-red-600 bg-red-50 rounded-full">
                      {(editedPackage.exclusions || []).length}
                    </span>
                  </div>
                  {expandedSection === "exclusions" ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                <AnimatePresence>
                  {expandedSection === "exclusions" && (
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: "auto" }}
                      exit={{ height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="p-4 space-y-3 border-t border-gray-200">
                        {(editedPackage.exclusions || []).map((exclusion, idx) => (
                          <div
                            key={exclusion.id}
                            className="flex gap-2 items-start p-3 bg-red-50 rounded-lg border border-red-200"
                          >
                            <textarea
                              value={exclusion.text || ""}
                              onChange={(e) =>
                                updateExclusion(exclusion.id, e.target.value)
                              }
                              placeholder="e.g., Pre-existing medical conditions not covered, or War-related injuries excluded"
                              rows={2}
                              className="flex-1 px-3 py-2 text-sm rounded-lg border border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500"
                            />
                            <button
                              onClick={() => removeExclusion(exclusion.id)}
                              className="p-1 text-red-600 rounded transition-colors hover:bg-red-100"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={addExclusion}
                          className="flex items-center px-4 py-2 w-full text-sm font-medium text-red-700 bg-red-50 rounded-lg border border-red-200 transition-colors hover:bg-red-100"
                        >
                          <Plus className="mr-2 w-4 h-4" />
                          Add Exclusion
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Eligibility Criteria Section */}
              <div className="overflow-hidden bg-white rounded-lg border-2 border-gray-200 shadow-sm">
                <button
                  onClick={() => toggleSection("eligibility")}
                  className="flex justify-between items-center px-4 py-3 w-full text-left transition-colors hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-semibold text-gray-900">
                      Eligibility Criteria
                    </span>
                    <span className="px-2 py-0.5 text-xs font-medium text-red-600 bg-red-50 rounded-full">
                      Required
                    </span>
                    <span className="px-2 py-0.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-full">
                      {(editedPackage.eligibility_criteria || []).length}
                    </span>
                  </div>
                  {expandedSection === "eligibility" ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                <AnimatePresence>
                  {expandedSection === "eligibility" && (
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: "auto" }}
                      exit={{ height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="p-4 space-y-3 border-t border-gray-200">
                        {(editedPackage.eligibility_criteria || []).map((criterion, idx) => (
                          <div
                            key={idx}
                            className="flex gap-2 items-start p-3 bg-blue-50 rounded-lg border border-blue-200"
                          >
                            <input
                              type="text"
                              value={criterion || ""}
                              onChange={(e) => updateEligibility(idx, e.target.value)}
                              placeholder="e.g., Must be 18 years or older, or Valid identification required"
                              className="flex-1 px-3 py-2 text-sm rounded-lg border border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <button
                              onClick={() => removeEligibility(idx)}
                              className="p-1 text-red-600 rounded transition-colors hover:bg-red-100"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={addEligibility}
                          className="flex items-center px-4 py-2 w-full text-sm font-medium text-blue-700 bg-blue-50 rounded-lg border border-blue-200 transition-colors hover:bg-blue-100"
                        >
                          <Plus className="mr-2 w-4 h-4" />
                          Add Criterion
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Claim Process Steps Section */}
              <div className="overflow-hidden bg-white rounded-lg border-2 border-gray-200 shadow-sm">
                <button
                  onClick={() => toggleSection("claims")}
                  className="flex justify-between items-center px-4 py-3 w-full text-left transition-colors hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-2">
                    <FileText className="w-5 h-5 text-purple-600" />
                    <span className="text-sm font-semibold text-gray-900">
                      Claim Process Steps
                    </span>
                    <span className="px-2 py-0.5 text-xs font-medium text-red-600 bg-red-50 rounded-full">
                      Required
                    </span>
                    <span className="px-2 py-0.5 text-xs font-medium text-purple-600 bg-purple-50 rounded-full">
                      {(editedPackage.claim_process_steps || []).length}
                    </span>
                  </div>
                  {expandedSection === "claims" ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                <AnimatePresence>
                  {expandedSection === "claims" && (
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: "auto" }}
                      exit={{ height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="p-4 space-y-3 border-t border-gray-200">
                        {(editedPackage.claim_process_steps || []).map((step, idx) => (
                          <div
                            key={step.id}
                            className="p-3 bg-purple-50 rounded-lg border border-purple-200"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <span className="text-xs font-medium text-purple-700">
                                Step #{step.step_number}
                              </span>
                              <button
                                onClick={() => removeClaimStep(step.id)}
                                className="p-1 text-red-600 rounded transition-colors hover:bg-red-100"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                              <input
                                type="text"
                                value={step.title || ""}
                                onChange={(e) =>
                                  updateClaimStep(step.id, "title", e.target.value)
                                }
                                placeholder="e.g., Report Incident"
                                className="px-3 py-2 text-sm rounded-lg border border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                              />
                              <input
                                type="text"
                                value={step.timeframe || ""}
                                onChange={(e) =>
                                  updateClaimStep(
                                    step.id,
                                    "timeframe",
                                    e.target.value
                                  )
                                }
                                placeholder="e.g., Within 24 hours"
                                className="px-3 py-2 text-sm rounded-lg border border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                              />
                              <textarea
                                value={step.description || ""}
                                onChange={(e) =>
                                  updateClaimStep(
                                    step.id,
                                    "description",
                                    e.target.value
                                  )
                                }
                                placeholder="e.g., Contact our support team immediately after the incident occurs and provide basic details"
                                rows={2}
                                className="px-3 py-2 text-sm rounded-lg border border-purple-300 md:col-span-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                              />
                              <input
                                type="text"
                                value={step.requirements || ""}
                                onChange={(e) =>
                                  updateClaimStep(
                                    step.id,
                                    "requirements",
                                    e.target.value
                                  )
                                }
                                placeholder="e.g., Police report, Medical certificate, Policy number"
                                className="px-3 py-2 text-sm rounded-lg border border-purple-300 md:col-span-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                              />
                            </div>
                          </div>
                        ))}
                        <button
                          onClick={addClaimStep}
                          className="flex items-center px-4 py-2 w-full text-sm font-medium text-purple-700 bg-purple-50 rounded-lg border border-purple-200 transition-colors hover:bg-purple-100"
                        >
                          <Plus className="mr-2 w-4 h-4" />
                          Add Claim Step
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Terms & Conditions Section */}
              <div className="overflow-hidden bg-white rounded-lg border-2 border-gray-200 shadow-sm">
                <button
                  onClick={() => toggleSection("terms")}
                  className="flex justify-between items-center px-4 py-3 w-full text-left transition-colors hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-2">
                    <FileText className="w-5 h-5 text-indigo-600" />
                    <span className="text-sm font-semibold text-gray-900">
                      Terms & Conditions
                    </span>
                    <span className="px-2 py-0.5 text-xs font-medium text-red-600 bg-red-50 rounded-full">
                      Required
                    </span>
                  </div>
                  {expandedSection === "terms" ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                <AnimatePresence>
                  {expandedSection === "terms" && (
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: "auto" }}
                      exit={{ height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="p-4 border-t border-gray-200">
                        <div className="mb-3">
                          <label className="block mb-2 text-sm font-medium text-gray-700">
                            Input Type
                          </label>
                          <div className="flex gap-2">
                            <button
                              onClick={() =>
                                updateField("termsInputType", "text")
                              }
                              className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                                (editedPackage.termsInputType || "text") === "text"
                                  ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              Text Input
                            </button>
                            <button
                              onClick={() =>
                                updateField("termsInputType", "url")
                              }
                              className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                                editedPackage.termsInputType === "url"
                                  ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              URL Link
                            </button>
                          </div>
                        </div>
                        {(editedPackage.termsInputType || "text") === "url" ? (
                          <input
                            type="url"
                            value={editedPackage.terms || ""}
                            onChange={(e) =>
                              updateField("terms", e.target.value)
                            }
                            placeholder="e.g., https://example.com/terms-and-conditions"
                            className="px-4 py-2.5 w-full text-sm rounded-lg border border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        ) : (
                          <textarea
                            value={editedPackage.terms || ""}
                            onChange={(e) =>
                              updateField("terms", e.target.value)
                            }
                            placeholder="e.g., This policy is subject to the following terms: Coverage is valid for 12 months from purchase date. Claims must be filed within 30 days of incident. All payments subject to verification..."
                            rows={6}
                            className="px-4 py-2.5 w-full text-sm rounded-lg border border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 px-6 py-4 bg-white border-t border-gray-200 shadow-lg">
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-3 font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex-1 px-4 py-3 font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg transition-all hover:from-blue-700 hover:to-indigo-700"
              >
                <div className="flex justify-center items-center">
                  <Check className="mr-2 w-5 h-5" />
                  Save Package
                </div>
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
});

// Compact Package Preview Card
export const PackagePreviewCard = memo(function PackagePreviewCard({
  package: pkg,
  index,
  onEdit,
  onRemove,
  insuranceType = "life",
}) {
  const benefitsCount = (pkg.insurance_coverage_list || []).length;
  const exclusionsCount = (pkg.exclusions || []).length;
  const eligibilityCount = (pkg.eligibility_criteria || []).filter(c => c && c.trim()).length;
  const claimStepsCount = (pkg.claim_process_steps || []).length;
  const hasTerms = pkg.terms && pkg.terms.trim() !== "";

  const hasValidMaxClaims = Number.isInteger(Number(pkg.max_claims)) && Number(pkg.max_claims) >= 1 && Number(pkg.max_claims) <= 999;
  const isQuoteOnly = !!pkg.is_quote_only;
  const quoteFieldsCount = (pkg.quote_request_fields || []).filter((f) => f?.key?.trim() && f?.label?.trim()).length;
  const isComplete = 
    pkg.productName?.trim() &&
    pkg.durationValue &&
    pkg.description?.trim() &&
    hasValidMaxClaims &&
    benefitsCount > 0 &&
    eligibilityCount > 0 &&
    claimStepsCount > 0 &&
    hasTerms &&
    (isQuoteOnly ? quoteFieldsCount > 0 : (pkg.premium && parseFloat(pkg.premium) > 0));

  // Helper function to format duration with proper singular/plural
  const formatDuration = (value, unit) => {
    const numValue = parseInt(value) || 0;
    if (numValue === 0) return `0 ${unit}`;
    
    let formattedUnit = unit;
    // Handle new standardized duration units
    if (unit === "hours" || unit === "hour") {
      formattedUnit = numValue === 1 ? "hour" : "hours";
    } else if (unit === "days" || unit === "day") {
      formattedUnit = numValue === 1 ? "day" : "days";
    } else if (unit === "weeks" || unit === "week") {
      formattedUnit = numValue === 1 ? "week" : "weeks";
    } else if (unit === "months" || unit === "month") {
      formattedUnit = numValue === 1 ? "month" : "months";
    } else if (unit === "years") {
      formattedUnit = numValue === 1 ? "year" : "years";
    } else {
      // Legacy support for old units
      if (unit === "monthly") {
        formattedUnit = numValue === 1 ? "month" : "months";
      } else if (unit === "weekly") {
        formattedUnit = numValue === 1 ? "week" : "weeks";
      } else if (unit === "yearly") {
        formattedUnit = numValue === 1 ? "year" : "years";
      }
    }
    
    return `${numValue} ${formattedUnit}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="flex relative flex-col p-5 bg-gradient-to-br from-white to-blue-50 rounded-xl border-2 border-blue-200 shadow-md transition-all hover:shadow-lg"
    >
      {/* Status Badge */}
      <div className="absolute -top-2 -right-2">
        {isComplete ? (
          <div className="flex items-center px-3 py-1 text-xs font-bold text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-full shadow-lg">
            <Check className="mr-1 w-3 h-3" />
            Complete
          </div>
        ) : (
          <div className="px-3 py-1 text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-red-500 rounded-full shadow-lg">
            Incomplete
          </div>
        )}
      </div>

      {/* Card header: number, title (one line), badge */}
      <div className="flex items-center gap-3 pr-20 mb-3 min-w-0">
        <div className="flex flex-shrink-0 justify-center items-center w-10 h-10 font-bold text-white bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg shadow-md">
          {index + 1}
        </div>
        <div className="flex-1 min-w-0 flex items-center gap-2 overflow-hidden">
          <h3 className="text-base font-bold text-gray-900 min-w-0 truncate flex-1" title={pkg.productName || `Package #${index + 1}`}>
            {pkg.productName || `Package #${index + 1}`}
          </h3>
          {/* {pkg.is_quote_only && (
            <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium text-amber-800 bg-amber-100 border border-amber-300 rounded-full">
              Quote-Based
            </span>
          )} */}
        </div>
      </div>
      <p className="text-sm text-gray-600 mb-3">
        {pkg.is_quote_only ? (
          <span className="text-amber-700 font-medium">Price on quote</span>
        ) : (
          <>{formatMoney(pkg.premium || 0)}/{pkg.premiumDuration || "monthly"}</>
        )}
        {" • "}
        {formatDuration(pkg.durationValue, pkg.durationUnit || "years")}
      </p>

      {/* Card Content - grows to fill available space */}
      <div className="flex-1">
        {/* Description */}
        {pkg.description && (
          <p className="mb-3 text-sm text-gray-600 line-clamp-2">
            {pkg.description}
          </p>
        )}

        {/* Policy Stats Grid */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="p-2 text-center bg-green-50 rounded-lg border border-green-200">
            <div className="text-xs text-green-600">Benefits</div>
            <div className="text-lg font-bold text-green-700">{benefitsCount}</div>
          </div>
          <div className="p-2 text-center bg-blue-50 rounded-lg border border-blue-200">
            <div className="text-xs text-blue-600">Eligibility</div>
            <div className="text-lg font-bold text-blue-700">{eligibilityCount}</div>
          </div>
          <div className="p-2 text-center bg-purple-50 rounded-lg border border-purple-200">
            <div className="text-xs text-purple-600">Claims</div>
            <div className="text-lg font-bold text-purple-700">{claimStepsCount}</div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="inline-flex items-center px-2 py-1 text-red-600 bg-red-50 rounded-full border border-red-200">
            <AlertCircle className="mr-1 w-3 h-3" />
            {exclusionsCount} Exclusions
          </span>
          <span className={`inline-flex items-center px-2 py-1 rounded-full border ${
            hasTerms 
              ? "text-indigo-600 bg-indigo-50 border-indigo-200" 
              : "text-gray-500 bg-gray-50 border-gray-200"
          }`}>
            <FileText className="mr-1 w-3 h-3" />
            {hasTerms ? "Terms Added" : "No Terms"}
          </span>
        </div>
      </div>

      {/* Card Footer - always at bottom */}
      <div className="flex gap-2 pt-4 mt-4 border-t border-blue-200">
        <button
          onClick={() => onEdit(pkg)}
          className="flex flex-1 justify-center items-center px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg border border-blue-200 transition-colors hover:bg-blue-100"
        >
          <Edit2 className="mr-2 w-4 h-4" />
          Edit
        </button>
        <button
          onClick={() => onRemove(pkg.id)}
          className="flex justify-center items-center px-4 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg border border-red-200 transition-colors hover:bg-red-100"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
});

// OLD Package Item for Life, General, and Travel Insurance
export const PackageItem = memo(function PackageItem({
  index,
  package: pkg,
  onChangeField,
  onRemove,
  insuranceType = "life", // "life", "general", or "travel"
}) {
  const [expandedSection, setExpandedSection] = useState(null);

  // Helper functions for managing nested arrays
  const addBenefit = () => {
    const benefits = pkg.insurance_coverage_list || [];
    const newBenefit = {
      id: Date.now() + Math.random(),
      title: "",
      description: "",
      limit: "",
    };
    onChangeField(pkg.id, "insurance_coverage_list", [...benefits, newBenefit]);
  };

  const updateBenefit = (benefitId, field, value) => {
    const benefits = pkg.insurance_coverage_list || [];
    const updated = benefits.map((b) =>
      b.id === benefitId ? { ...b, [field]: value } : b
    );
    onChangeField(pkg.id, "insurance_coverage_list", updated);
  };

  const removeBenefit = (benefitId) => {
    const benefits = pkg.insurance_coverage_list || [];
    const filtered = benefits.filter((b) => b.id !== benefitId);
    onChangeField(pkg.id, "insurance_coverage_list", filtered);
  };

  const addExclusion = () => {
    const exclusions = pkg.exclusions || [];
    const newExclusion = {
      id: Date.now() + Math.random(),
      text: "",
    };
    onChangeField(pkg.id, "exclusions", [...exclusions, newExclusion]);
  };

  const updateExclusion = (exclusionId, value) => {
    const exclusions = pkg.exclusions || [];
    const updated = exclusions.map((e) =>
      e.id === exclusionId ? { ...e, text: value } : e
    );
    onChangeField(pkg.id, "exclusions", updated);
  };

  const removeExclusion = (exclusionId) => {
    const exclusions = pkg.exclusions || [];
    const filtered = exclusions.filter((e) => e.id !== exclusionId);
    onChangeField(pkg.id, "exclusions", filtered);
  };

  const addEligibility = () => {
    const criteria = pkg.eligibility_criteria || [];
    onChangeField(pkg.id, "eligibility_criteria", [...criteria, ""]);
  };

  const updateEligibility = (idx, value) => {
    const criteria = pkg.eligibility_criteria || [];
    const updated = [...criteria];
    updated[idx] = value;
    onChangeField(pkg.id, "eligibility_criteria", updated);
  };

  const removeEligibility = (idx) => {
    const criteria = pkg.eligibility_criteria || [];
    const filtered = criteria.filter((_, i) => i !== idx);
    onChangeField(pkg.id, "eligibility_criteria", filtered);
  };

  const addClaimStep = () => {
    const steps = pkg.claim_process_steps || [];
    const newStep = {
      id: Date.now() + Math.random(),
      step_number: steps.length + 1,
      title: "",
      description: "",
      timeframe: "",
      requirements: "",
    };
    onChangeField(pkg.id, "claim_process_steps", [...steps, newStep]);
  };

  const updateClaimStep = (stepId, field, value) => {
    const steps = pkg.claim_process_steps || [];
    const updated = steps.map((s) =>
      s.id === stepId ? { ...s, [field]: value } : s
    );
    onChangeField(pkg.id, "claim_process_steps", updated);
  };

  const removeClaimStep = (stepId) => {
    const steps = pkg.claim_process_steps || [];
    const filtered = steps.filter((s) => s.id !== stepId);
    // Renumber remaining steps
    const renumbered = filtered.map((s, idx) => ({
      ...s,
      step_number: idx + 1,
    }));
    onChangeField(pkg.id, "claim_process_steps", renumbered);
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 bg-gradient-to-br from-white to-gray-50 rounded-xl border-2 border-gray-200 shadow-sm"
    >
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center space-x-3">
          <div className="flex justify-center items-center w-10 h-10 font-bold text-white bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
            {index + 1}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Package #{index + 1}
            </h3>
            <p className="text-xs text-gray-500">
              Configure package details and policy terms
            </p>
          </div>
        </div>
        <button
          onClick={() => onRemove(pkg.id)}
          className="p-2 text-red-600 rounded-lg transition-colors hover:bg-red-50"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Basic Package Info */}
      <div className="p-4 mb-4 bg-white rounded-lg border border-gray-200">
        <h4 className="mb-3 text-sm font-semibold text-gray-700">
          Basic Information
        </h4>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="block mb-1 text-xs font-medium text-gray-600">
              Product Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={pkg.productName || ""}
              onChange={(e) =>
                onChangeField(pkg.id, "productName", e.target.value)
              }
              placeholder="e.g., Premium Life Plan"
              className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="md:col-span-2 p-3 rounded-lg border border-amber-200 bg-amber-50">
            <label className="flex gap-2 items-center cursor-pointer">
              <input
                type="checkbox"
                checked={!!pkg.is_quote_only}
                onChange={(e) => {
                  const checked = e.target.checked;
                  onChangeField(pkg.id, "is_quote_only", checked);
                  if (checked) onChangeField(pkg.id, "premium", "");
                }}
                className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <span className="text-xs font-semibold text-gray-800">Quote-Based Only (price on quote; premium sent as 0)</span>
            </label>
          </div>
          {!pkg.is_quote_only && (
          <div>
            <label className="block mb-1 text-xs font-medium text-gray-600">
              Premium (₵) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={pkg.premium || ""}
              onChange={(e) => onChangeField(pkg.id, "premium", e.target.value)}
              placeholder="e.g., 150.00"
              className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          )}
          <div className="md:col-span-2">
            <label className="block mb-1 text-xs font-medium text-gray-600">
              Duration <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <div className="flex-1">
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={pkg.durationValue || ""}
                  onChange={(e) =>
                    onChangeField(pkg.id, "durationValue", e.target.value)
                  }
                  placeholder="Enter number"
                  className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="w-36">
                <select
                  value={
                    pkg.durationUnit ||
                    (insuranceType === "travel" ? "hours" : "years")
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    onChangeField(pkg.id, "durationUnit", value);
                  }}
                  className="px-3 py-2 w-full bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {insuranceType === "travel" || insuranceType === "life" || insuranceType === "general" ? (
                    <>
                      <option value="hours">Hours</option>
                      <option value="hour">Hour</option>
                      <option value="day">Day</option>
                      <option value="days">Days</option>
                      <option value="week">Week</option>
                      <option value="weeks">Weeks</option>
                      <option value="month">Month</option>
                      <option value="months">Months</option>
                      <option value="years">Years</option>
                    </>
                  ) : (
                    <>
                      <option value="years">Years</option>
                      <option value="months">Months</option>
                    </>
                  )}
                </select>
              </div>
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="block mb-1 text-xs font-medium text-gray-600">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={pkg.description || ""}
              onChange={(e) =>
                onChangeField(pkg.id, "description", e.target.value)
              }
              placeholder="Describe what this package includes..."
              rows={3}
              className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block mb-1 text-xs font-medium text-gray-600">
              Max Claims <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min={1}
              max={999}
              value={pkg.max_claims ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                onChangeField(pkg.id, "max_claims", val === "" ? "" : Number(val));
              }}
              placeholder="e.g. 3"
              className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Collapsible Sections */}
      <div className="space-y-3">
        {/* Benefits Section */}
        <div className="overflow-hidden bg-white rounded-lg border border-gray-200">
          <button
            onClick={() => toggleSection("benefits")}
            className="flex justify-between items-center px-4 py-3 w-full text-left transition-colors hover:bg-gray-50"
          >
            <div className="flex items-center space-x-2">
              <Shield className="w-4 h-4 text-green-600" />
              <span className="text-sm font-semibold text-gray-900">
                Coverage Benefits
              </span>
              <span className="px-2 py-0.5 text-xs font-medium text-green-600 bg-green-50 rounded-full">
                {(pkg.insurance_coverage_list || []).length}
              </span>
            </div>
            {expandedSection === "benefits" ? (
              <ChevronUp className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </button>
          <AnimatePresence>
            {expandedSection === "benefits" && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: "auto" }}
                exit={{ height: 0 }}
                className="overflow-hidden"
              >
                <div className="p-4 space-y-3 border-t border-gray-200">
                  {(pkg.insurance_coverage_list || []).map((benefit, idx) => (
                    <div
                      key={benefit.id}
                      className="p-3 bg-green-50 rounded-lg border border-green-200"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-xs font-medium text-green-700">
                          Benefit #{idx + 1}
                        </span>
                        <button
                          onClick={() => removeBenefit(benefit.id)}
                          className="p-1 text-red-600 rounded transition-colors hover:bg-red-100"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={benefit.title || ""}
                          onChange={(e) =>
                            updateBenefit(benefit.id, "title", e.target.value)
                          }
                          placeholder="Benefit title"
                          className="px-2 py-1.5 w-full text-sm rounded border border-green-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                        <textarea
                          value={benefit.description || ""}
                          onChange={(e) =>
                            updateBenefit(
                              benefit.id,
                              "description",
                              e.target.value
                            )
                          }
                          placeholder="Benefit description"
                          rows={2}
                          className="px-2 py-1.5 w-full text-sm rounded border border-green-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                        <input
                          type="text"
                          value={benefit.limit || ""}
                          onChange={(e) =>
                            updateBenefit(benefit.id, "limit", e.target.value)
                          }
                          placeholder="Coverage limit (optional)"
                          className="px-2 py-1.5 w-full text-sm rounded border border-green-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={addBenefit}
                    className="flex items-center px-3 py-2 w-full text-sm font-medium text-green-700 bg-green-50 rounded-lg border border-green-200 transition-colors hover:bg-green-100"
                  >
                    <Plus className="mr-2 w-4 h-4" />
                    Add Benefit
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Exclusions Section */}
        <div className="overflow-hidden bg-white rounded-lg border border-gray-200">
          <button
            onClick={() => toggleSection("exclusions")}
            className="flex justify-between items-center px-4 py-3 w-full text-left transition-colors hover:bg-gray-50"
          >
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <span className="text-sm font-semibold text-gray-900">
                Exclusions
              </span>
              <span className="px-2 py-0.5 text-xs font-medium text-red-600 bg-red-50 rounded-full">
                {(pkg.exclusions || []).length}
              </span>
            </div>
            {expandedSection === "exclusions" ? (
              <ChevronUp className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </button>
          <AnimatePresence>
            {expandedSection === "exclusions" && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: "auto" }}
                exit={{ height: 0 }}
                className="overflow-hidden"
              >
                <div className="p-4 space-y-3 border-t border-gray-200">
                  {(pkg.exclusions || []).map((exclusion, idx) => (
                    <div
                      key={exclusion.id}
                      className="flex gap-2 items-start p-3 bg-red-50 rounded-lg border border-red-200"
                    >
                      <textarea
                        value={exclusion.text || ""}
                        onChange={(e) =>
                          updateExclusion(exclusion.id, e.target.value)
                        }
                        placeholder={`Exclusion #${idx + 1}`}
                        rows={2}
                        className="flex-1 px-2 py-1.5 text-sm rounded border border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                      <button
                        onClick={() => removeExclusion(exclusion.id)}
                        className="p-1 text-red-600 rounded transition-colors hover:bg-red-100"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={addExclusion}
                    className="flex items-center px-3 py-2 w-full text-sm font-medium text-red-700 bg-red-50 rounded-lg border border-red-200 transition-colors hover:bg-red-100"
                  >
                    <Plus className="mr-2 w-4 h-4" />
                    Add Exclusion
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Eligibility Criteria Section */}
        <div className="overflow-hidden bg-white rounded-lg border border-gray-200">
          <button
            onClick={() => toggleSection("eligibility")}
            className="flex justify-between items-center px-4 py-3 w-full text-left transition-colors hover:bg-gray-50"
          >
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-gray-900">
                Eligibility Criteria
              </span>
              <span className="px-2 py-0.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-full">
                {(pkg.eligibility_criteria || []).length}
              </span>
            </div>
            {expandedSection === "eligibility" ? (
              <ChevronUp className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </button>
          <AnimatePresence>
            {expandedSection === "eligibility" && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: "auto" }}
                exit={{ height: 0 }}
                className="overflow-hidden"
              >
                <div className="p-4 space-y-3 border-t border-gray-200">
                  {(pkg.eligibility_criteria || []).map((criterion, idx) => (
                    <div
                      key={idx}
                      className="flex gap-2 items-start p-3 bg-blue-50 rounded-lg border border-blue-200"
                    >
                      <input
                        type="text"
                        value={criterion || ""}
                        onChange={(e) => updateEligibility(idx, e.target.value)}
                        placeholder={`Criterion #${idx + 1}`}
                        className="flex-1 px-2 py-1.5 text-sm rounded border border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={() => removeEligibility(idx)}
                        className="p-1 text-red-600 rounded transition-colors hover:bg-red-100"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={addEligibility}
                    className="flex items-center px-3 py-2 w-full text-sm font-medium text-blue-700 bg-blue-50 rounded-lg border border-blue-200 transition-colors hover:bg-blue-100"
                  >
                    <Plus className="mr-2 w-4 h-4" />
                    Add Criterion
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Claim Process Steps Section */}
        <div className="overflow-hidden bg-white rounded-lg border border-gray-200">
          <button
            onClick={() => toggleSection("claims")}
            className="flex justify-between items-center px-4 py-3 w-full text-left transition-colors hover:bg-gray-50"
          >
            <div className="flex items-center space-x-2">
              <FileText className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-semibold text-gray-900">
                Claim Process Steps
              </span>
              <span className="px-2 py-0.5 text-xs font-medium text-purple-600 bg-purple-50 rounded-full">
                {(pkg.claim_process_steps || []).length}
              </span>
            </div>
            {expandedSection === "claims" ? (
              <ChevronUp className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </button>
          <AnimatePresence>
            {expandedSection === "claims" && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: "auto" }}
                exit={{ height: 0 }}
                className="overflow-hidden"
              >
                <div className="p-4 space-y-3 border-t border-gray-200">
                  {(pkg.claim_process_steps || []).map((step, idx) => (
                    <div
                      key={step.id}
                      className="p-3 bg-purple-50 rounded-lg border border-purple-200"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-xs font-medium text-purple-700">
                          Step #{step.step_number}
                        </span>
                        <button
                          onClick={() => removeClaimStep(step.id)}
                          className="p-1 text-red-600 rounded transition-colors hover:bg-red-100"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                        <input
                          type="text"
                          value={step.title || ""}
                          onChange={(e) =>
                            updateClaimStep(step.id, "title", e.target.value)
                          }
                          placeholder="Step title"
                          className="px-2 py-1.5 text-sm rounded border border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                        <input
                          type="text"
                          value={step.timeframe || ""}
                          onChange={(e) =>
                            updateClaimStep(
                              step.id,
                              "timeframe",
                              e.target.value
                            )
                          }
                          placeholder="Timeframe (e.g., 24 hours)"
                          className="px-2 py-1.5 text-sm rounded border border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                        <textarea
                          value={step.description || ""}
                          onChange={(e) =>
                            updateClaimStep(
                              step.id,
                              "description",
                              e.target.value
                            )
                          }
                          placeholder="Step description"
                          rows={2}
                          className="px-2 py-1.5 text-sm rounded border border-purple-300 md:col-span-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                        <input
                          type="text"
                          value={step.requirements || ""}
                          onChange={(e) =>
                            updateClaimStep(
                              step.id,
                              "requirements",
                              e.target.value
                            )
                          }
                          placeholder="Requirements"
                          className="px-2 py-1.5 text-sm rounded border border-purple-300 md:col-span-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={addClaimStep}
                    className="flex items-center px-3 py-2 w-full text-sm font-medium text-purple-700 bg-purple-50 rounded-lg border border-purple-200 transition-colors hover:bg-purple-100"
                  >
                    <Plus className="mr-2 w-4 h-4" />
                    Add Claim Step
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Terms & Conditions Section */}
        <div className="overflow-hidden bg-white rounded-lg border border-gray-200">
          <button
            onClick={() => toggleSection("terms")}
            className="flex justify-between items-center px-4 py-3 w-full text-left transition-colors hover:bg-gray-50"
          >
            <div className="flex items-center space-x-2">
              <FileText className="w-4 h-4 text-indigo-600" />
              <span className="text-sm font-semibold text-gray-900">
                Terms & Conditions
              </span>
            </div>
            {expandedSection === "terms" ? (
              <ChevronUp className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </button>
          <AnimatePresence>
            {expandedSection === "terms" && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: "auto" }}
                exit={{ height: 0 }}
                className="overflow-hidden"
              >
                <div className="p-4 border-t border-gray-200">
                  <div className="mb-3">
                    <label className="block mb-2 text-xs font-medium text-gray-600">
                      Input Type
                    </label>
                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          onChangeField(pkg.id, "termsInputType", "text")
                        }
                        className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                          (pkg.termsInputType || "text") === "text"
                            ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                            : "border-gray-300 text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        Text Input
                      </button>
                      <button
                        onClick={() =>
                          onChangeField(pkg.id, "termsInputType", "url")
                        }
                        className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                          pkg.termsInputType === "url"
                            ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                            : "border-gray-300 text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        URL Link
                      </button>
                    </div>
                  </div>
                  {(pkg.termsInputType || "text") === "url" ? (
                    <input
                      type="url"
                      value={pkg.terms || ""}
                      onChange={(e) =>
                        onChangeField(pkg.id, "terms", e.target.value)
                      }
                      placeholder="https://example.com/terms"
                      className="px-3 py-2 w-full text-sm rounded-lg border border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  ) : (
                    <textarea
                      value={pkg.terms || ""}
                      onChange={(e) =>
                        onChangeField(pkg.id, "terms", e.target.value)
                      }
                      placeholder="Enter terms and conditions for this package..."
                      rows={6}
                      className="px-3 py-2 w-full text-sm rounded-lg border border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
});

// Exclusion Item
export const ExclusionItem = memo(function ExclusionItem({
  index,
  item,
  onChangeField,
  onRemove,
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 bg-red-50 rounded-lg border border-red-200"
    >
      <div className="flex justify-between items-center mb-3">
        <span className="text-sm font-medium text-gray-700">
          Exclusion #{index + 1}
        </span>
        <button
          onClick={() => onRemove(item.id)}
          className="p-1 text-red-600 rounded-lg transition-colors hover:bg-red-100"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div>
        <label className="block mb-1 text-xs font-medium text-gray-600">
          Exclusion Description
        </label>
        <textarea
          value={item.text}
          onChange={(e) => onChangeField(item.id, "text", e.target.value)}
          placeholder="Describe what is NOT covered..."
          rows={2}
          className="px-3 py-2 w-full bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500"
        />
      </div>
    </motion.div>
  );
});

// =========================
// STEP 1: Insurance Type Selection
// =========================
export function StepInsuranceType({
  formData,
  updateFormData,
  validationErrors,
}) {
  const insuranceTypes = [
    {
      id: "tripsecure",
      name: "TripSecure Insurance",
      description: "Reliable coverage for every journey on the road.",
      icon: Car,
      color: "blue",
      isDefault: true,
    },
    {
      id: "travel",
      name: "Travel Insurance",
      description: "Coverage for travel-related risks",
      icon: Plane,
      color: "indigo",
      isDefault: false,
    },
    {
      id: "life",
      name: "Life Insurance",
      description: "Protection for life and financial security",
      icon: Shield,
      color: "green",
      isDefault: false,
    },
    {
      id: "general",
      name: "General Insurance",
      description: "Coverage for non-life insurance needs",
      icon: Home,
      color: "purple",
      isDefault: false,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <div className="text-center">
        <h2 className="mb-1 text-lg font-semibold text-gray-900">
          Select Insurance Type
        </h2>
        <p className="text-sm text-gray-600">
          Choose the type of insurance policy you want to create
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {insuranceTypes.map((type) => {
          const isSelected = formData.insuranceType === type.id;

          const getCardClasses = () => {
            if (isSelected) {
              switch (type.color) {
                case "blue":
                  return "border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-xl ring-2 ring-blue-200";
                case "green":
                  return "border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 shadow-xl ring-2 ring-green-200";
                case "purple":
                  return "border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50 shadow-xl ring-2 ring-purple-200";
                default:
                  return "border-gray-500 bg-gray-50 shadow-xl ring-2 ring-gray-200";
              }
            }
            return "border-gray-200 hover:border-gray-300 hover:shadow-lg bg-white";
          };

          const getIconBgClasses = () => {
            if (isSelected) {
              switch (type.color) {
                case "blue":
                  return "bg-gradient-to-br from-blue-100 to-indigo-100";
                case "green":
                  return "bg-gradient-to-br from-green-100 to-emerald-100";
                case "purple":
                  return "bg-gradient-to-br from-purple-100 to-pink-100";
                default:
                  return "bg-gray-100";
              }
            }
            return "bg-gray-100";
          };

          return (
            <motion.div
              key={type.id}
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => updateFormData("insuranceType", type.id)}
              className={`relative p-4 rounded-xl transition-all duration-300 cursor-pointer ${getCardClasses()}`}
            >
              {/* Default Badge */}
              {type.isDefault && (
                <div className="absolute top-3 right-3 px-2 py-1 text-xs font-bold text-blue-900 bg-blue-200 rounded-full"></div>
              )}

              {/* Selection Indicator */}
              {isSelected && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="flex absolute top-3 right-3 justify-center items-center w-6 h-6 bg-green-500 rounded-full"
                >
                  <CheckCircle className="w-4 h-4 text-white" />
                </motion.div>
              )}

              {/* Icon Section */}
              {type.id === "tripsecure" ? (
                <div className="flex flex-col items-center mb-3 text-center justfy-between">
                  <img src={Images.logo_colored} className="w-32" alt="TripSecure Loo" />
                  <h3 className="mb-1 text-base font-bold text-gray-900">
                    {type.name}
                  </h3>
                  <p className="text-xs leading-relaxed text-gray-600">
                    {type.description}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col justify-between items-center mb-3 text-center">
                  <div
                    className={`inline-flex justify-center items-center mb-2 w-12 h-12 rounded-xl ${getIconBgClasses()}`}
                  >
                    {(() => {
                      const Icon = type.icon;
                      return (
                        <Icon
                          className={`w-6 h-6 ${
                            isSelected ? "text-gray-800" : "text-gray-500"
                          }`}
                        />
                      );
                    })()}
                  </div>
                  <h3 className="mb-1 text-base font-bold text-gray-900">
                    {type.name}
                  </h3>
                  <p className="text-xs leading-relaxed text-gray-600">
                    {type.description}
                  </p>
                </div>
              )}

              {/* Bottom Accent */}
              <div
                className={`mt-4 h-1 rounded-full ${
                  isSelected
                    ? type.color === "blue"
                      ? "bg-gradient-to-r from-blue-500 to-indigo-500"
                      : type.color === "green"
                      ? "bg-gradient-to-r from-green-500 to-emerald-500"
                      : "bg-gradient-to-r from-purple-500 to-pink-500"
                    : "bg-gray-200"
                }`}
              />
            </motion.div>
          );
        })}
      </div>

      {validationErrors.insuranceType && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-center items-center px-4 py-3 text-sm text-red-600 bg-red-50 rounded-lg"
        >
          <AlertCircle className="flex-shrink-0 mr-2 w-5 h-5" />
          {validationErrors.insuranceType}
        </motion.p>
      )}
    </motion.div>
  );
}

// =========================
// STEP 2: Basic Info
// =========================
export function StepBasicInfo({
  formData,
  updateFormData,
  policyTypes,
  validationErrors,
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      {/* Policy Name - Dynamic based on insurance type */}
      <div className="space-y-2">
        <label className="flex items-center text-sm font-semibold text-gray-700">
          Policy Name
          <Lock className="ml-2 w-4 h-4 text-gray-400" />
        </label>
        <div className="relative">
          <input
            type="text"
            value={formData.policyName || "TripSecure Road Trip Insurance"}
            disabled
            className="px-4 py-3 w-full font-semibold text-gray-600 bg-gray-100 rounded-xl border border-gray-300 cursor-not-allowed"
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="relative group">
              <Info className="w-5 h-5 text-gray-400" />
              <div className="absolute right-0 bottom-full px-3 py-2 mb-2 text-xs text-white whitespace-nowrap bg-gray-900 rounded-lg opacity-0 transition-opacity pointer-events-none group-hover:opacity-100">
                {formData.insuranceType === "tripsecure"
                  ? "Policy name is controlled by TripSecure"
                  : "Policy name is automatically generated based on your company name and insurance type"}
              </div>
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-500">
          {formData.insuranceType === "tripsecure"
            ? "All TripSecure policies are unified under the TripSecure Road Trip Insurance brand"
            : formData.insuranceType === "travel"
            ? "Policy name is generated as: [Company Name] Travel Insurance"
            : formData.insuranceType === "life"
            ? "Policy name is generated as: [Company Name] Life Insurance"
            : "Policy name is generated as: [Company Name] [Subcategory]"}
        </p>
      </div>

      {/* Max Claims - TripSecure only (per-package for Life/General/Travel is in package form) */}
      {formData.insuranceType === "tripsecure" && (
        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-gray-700">
            <Target className="mr-2 w-4 h-4 text-gray-500" />
            Max Claims <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min={1}
            max={999}
            value={formData.max_claims ?? ""}
            onChange={(e) => {
              const val = e.target.value;
              updateFormData("max_claims", val === "" ? "" : Number(val));
            }}
            placeholder="e.g. 3"
            className="px-4 py-3 w-full rounded-xl border border-gray-300 transition-all duration-200 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-xs text-gray-500">
            Maximum number of claims allowed per policy (per coverage period).
          </p>
          {validationErrors.max_claims && (
            <p className="flex items-center text-sm text-red-600">
              <AlertCircle className="mr-1 w-4 h-4" />
              {validationErrors.max_claims}
            </p>
          )}
        </div>
      )}

      {/* Policy Type Selection - Only for TripSecure Insurance */}
      {formData.insuranceType === "tripsecure" && (
        <div className="space-y-6">
          <div className="text-center">
            <label className="block mb-1 text-base font-semibold text-gray-800">
              Choose Your Policy Type <span className="text-red-500">*</span>
            </label>
            <p className="text-sm text-gray-600">
              Select the type of coverage that best fits your insurance offering
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {policyTypes.map((type) => {
              const isSelected = formData.policyType === type.id;
              const isDisabled = type.disabled;

              const getCardClasses = () => {
                if (isDisabled) {
                  return "border-gray-200 bg-gray-100 opacity-60 cursor-not-allowed";
                }
                if (isSelected) {
                  switch (type.color) {
                    case "blue":
                      return "border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-xl ring-2 ring-blue-200";
                    case "green":
                      return "border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 shadow-xl ring-2 ring-green-200";
                    case "orange":
                      return "border-orange-500 bg-gradient-to-br from-orange-50 to-amber-50 shadow-xl ring-2 ring-orange-200";
                    case "purple":
                      return "border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50 shadow-xl ring-2 ring-purple-200";
                    default:
                      return "border-gray-500 bg-gray-50 shadow-xl ring-2 ring-gray-200";
                  }
                }
                return "border-gray-200 hover:border-gray-300 hover:shadow-lg bg-white";
              };

              const getIconBgClasses = () => {
                if (isDisabled) return "bg-gray-200";
                if (isSelected) {
                  switch (type.color) {
                    case "blue":
                      return "bg-gradient-to-br from-blue-100 to-indigo-100";
                    case "green":
                      return "bg-gradient-to-br from-green-100 to-emerald-100";
                    case "orange":
                      return "bg-gradient-to-br from-orange-100 to-amber-100";
                    case "purple":
                      return "bg-gradient-to-br from-purple-100 to-pink-100";
                    default:
                      return "bg-gray-100";
                  }
                }
                return "bg-gray-100";
              };

              return (
                <motion.div
                  key={type.id}
                  whileHover={!isDisabled ? { scale: 1.02, y: -2 } : {}}
                  whileTap={!isDisabled ? { scale: 0.98 } : {}}
                  onClick={() =>
                    !isDisabled && updateFormData("policyType", type.id)
                  }
                  className={`relative p-4 rounded-xl ${
                    isDisabled
                      ? "":"cursor-pointer transition-all duration-300"} ${getCardClasses()}`}
                >
                  {/* Coming Soon Badge */}
                  {isDisabled && (
                    <div className="absolute top-2 right-2 px-2 py-1 text-xs font-bold text-yellow-900 bg-yellow-400 rounded-full">
                      COMING SOON
                    </div>
                  )}

                  {/* Selection Indicator */}
                  {isSelected && !isDisabled && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="flex absolute top-3 right-3 justify-center items-center w-5 h-5 bg-green-500 rounded-full"
                    >
                      <CheckCircle className="w-3 h-3 text-white" />
                    </motion.div>
                  )}

                  {/* Icon Section */}
                  <div className="mb-3 text-center">
                    <div
                      className={`inline-flex justify-center items-center mb-2 w-12 h-12 rounded-xl ${getIconBgClasses()}`}
                    >
                      {(() => {
                        const Icon = type.icon;
                        return (
                          <Icon
                            className={`w-6 h-6 ${
                              isDisabled
                                ? "text-gray-400"
                                : isSelected
                                ? "text-gray-800"
                                : "text-gray-500"
                            }`}
                          />
                        );
                      })()}
                    </div>
                    <h3 className="mb-1 text-lg font-bold text-gray-900">
                      {type.name}
                    </h3>
                    <p className="text-xs leading-relaxed text-gray-600">
                      {type.description}
                    </p>
                  </div>

                  {/* Features List */}
                  <div className="space-y-1">
                    {type.features.map((feature, index) => (
                      <motion.div
                        key={`feature-${type.id}-${index}`}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="flex items-center text-xs text-gray-700"
                      >
                        <CheckCircle className="mr-1.5 w-3 h-3 text-green-500 flex-shrink-0" />
                        <span>{feature}</span>
                      </motion.div>
                    ))}
                  </div>

                  {/* Bottom Accent */}
                  <div
                    className={`mt-3 h-0.5 rounded-full ${
                      isSelected && !isDisabled
                        ? type.color === "blue"
                          ? "bg-gradient-to-r from-blue-500 to-indigo-500"
                          : type.color === "green"
                          ? "bg-gradient-to-r from-green-500 to-emerald-500"
                          : type.color === "orange"
                          ? "bg-gradient-to-r from-orange-500 to-amber-500"
                          : "bg-gradient-to-r from-purple-500 to-pink-500"
                        : "bg-gray-200"
                    }`}
                  />
                </motion.div>
              );
            })}
          </div>

          {validationErrors.policyType && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-center items-center px-4 py-3 text-sm text-red-600 bg-red-50 rounded-lg"
            >
              <AlertCircle className="flex-shrink-0 mr-2 w-5 h-5" />
              {validationErrors.policyType}
            </motion.p>
          )}
        </div>
      )}

      {/* Subcategory Selection - Only for General Insurance */}
      {formData.insuranceType === "general" && (
        <div className="space-y-6">
          <div className="text-center">
            <label className="block mb-1 text-base font-semibold text-gray-800">
              Choose Subcategory <span className="text-red-500">*</span>
            </label>
            <p className="text-sm text-gray-600">
              Select the type of general insurance you want to offer
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                id: "health_insurance",
                name: "Health Insurance",
                description: "Medical coverage and healthcare protection",
                icon: Heart,
                color: "blue",
              },
              {
                id: "home_insurance",
                name: "Home Insurance",
                description: "Protection for your property and belongings",
                icon: Home,
                color: "green",
              },
              {
                id: "accident_insurance",
                name: "Accident Insurance",
                description: "Coverage for accidental injuries and events",
                icon: AlertTriangle,
                color: "orange",
              },
              {
                id: "fire_insurance",
                name: "Fire Insurance",
                description: "Protection against fire damage and losses",
                icon: Flame,
                color: "red",
              },
              {
                id: "marine_insurance",
                name: "Marine Insurance",
                description: "Coverage for cargo and maritime risks",
                icon: Ship,
                color: "indigo",
              },
              {
                id: "liability_insurance",
                name: "Liability Insurance",
                description: "Protection against legal claims and damages",
                icon: Scale,
                color: "purple",
              },
            ].map((subcategory) => {
              const isSelected = formData.subcategory === subcategory.id;

              const getCardClasses = () => {
                if (isSelected) {
                  switch (subcategory.color) {
                    case "blue":
                      return "border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-xl ring-2 ring-blue-200";
                    case "green":
                      return "border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 shadow-xl ring-2 ring-green-200";
                    case "orange":
                      return "border-orange-500 bg-gradient-to-br from-orange-50 to-amber-50 shadow-xl ring-2 ring-orange-200";
                    case "red":
                      return "border-red-500 bg-gradient-to-br from-red-50 to-pink-50 shadow-xl ring-2 ring-red-200";
                    case "indigo":
                      return "border-indigo-500 bg-gradient-to-br from-indigo-50 to-blue-50 shadow-xl ring-2 ring-indigo-200";
                    case "purple":
                      return "border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50 shadow-xl ring-2 ring-purple-200";
                    default:
                      return "border-gray-500 bg-gray-50 shadow-xl ring-2 ring-gray-200";
                  }
                }
                return "border-gray-200 hover:border-gray-300 hover:shadow-lg bg-white";
              };

              const getIconBgClasses = () => {
                if (isSelected) {
                  switch (subcategory.color) {
                    case "blue":
                      return "bg-gradient-to-br from-blue-100 to-indigo-100";
                    case "green":
                      return "bg-gradient-to-br from-green-100 to-emerald-100";
                    case "orange":
                      return "bg-gradient-to-br from-orange-100 to-amber-100";
                    case "red":
                      return "bg-gradient-to-br from-red-100 to-pink-100";
                    case "indigo":
                      return "bg-gradient-to-br from-indigo-100 to-blue-100";
                    case "purple":
                      return "bg-gradient-to-br from-purple-100 to-pink-100";
                    default:
                      return "bg-gray-100";
                  }
                }
                return "bg-gray-100";
              };

              return (
                <motion.div
                  key={subcategory.id}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => updateFormData("subcategory", subcategory.id)}
                  className={`relative p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer ${getCardClasses()}`}
                >
                  {/* Selection Indicator */}
                  {isSelected && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="flex absolute top-3 right-3 justify-center items-center w-6 h-6 bg-green-500 rounded-full"
                    >
                      <CheckCircle className="w-4 h-4 text-white" />
                    </motion.div>
                  )}

                  {/* Icon Section */}
                  <div className="mb-3 text-center">
                    <div
                      className={`inline-flex justify-center items-center mb-2 w-12 h-12 rounded-xl ${getIconBgClasses()}`}
                    >
                      {(() => {
                        const Icon = subcategory.icon;
                        return (
                          <Icon
                            className={`w-6 h-6 ${
                              isSelected ? "text-gray-800" : "text-gray-500"
                            }`}
                          />
                        );
                      })()}
                    </div>
                    <h3 className="mb-1 text-base font-bold text-gray-900">
                      {subcategory.name}
                    </h3>
                    <p className="text-xs leading-relaxed text-gray-600">
                      {subcategory.description}
                    </p>
                  </div>

                  {/* Bottom Accent */}
                  <div
                    className={`mt-3 h-0.5 rounded-full ${
                      isSelected
                        ? subcategory.color === "blue"
                          ? "bg-gradient-to-r from-blue-500 to-indigo-500"
                          : subcategory.color === "green"
                          ? "bg-gradient-to-r from-green-500 to-emerald-500"
                          : subcategory.color === "orange"
                          ? "bg-gradient-to-r from-orange-500 to-amber-500"
                          : subcategory.color === "red"
                          ? "bg-gradient-to-r from-red-500 to-pink-500"
                          : subcategory.color === "indigo"
                          ? "bg-gradient-to-r from-indigo-500 to-blue-500"
                          : "bg-gradient-to-r from-purple-500 to-pink-500"
                        : "bg-gray-200"
                    }`}
                  />
                </motion.div>
              );
            })}
          </div>

          {validationErrors.subcategory && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-center items-center px-4 py-3 text-sm text-red-600 bg-red-50 rounded-lg"
            >
              <AlertCircle className="flex-shrink-0 mr-2 w-5 h-5" />
              {validationErrors.subcategory}
            </motion.p>
          )}
        </div>
      )}

      {/* Description Fields - Hidden */}
      {/* <div className="space-y-2">
        <label className="block text-sm font-semibold text-gray-700">
          Policy Description <span className="text-red-500">*</span>
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => updateFormData("description", e.target.value)}
          placeholder="Provide a detailed description of the policy coverage, benefits, and key features..."
          rows={6}
          className={`w-full px-4 py-3 border rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            validationErrors.description
              ? "border-red-300 bg-red-50"
              : "border-gray-300 hover:border-gray-400"
          }`}
        />
        {validationErrors.description && (
          <p className="flex items-center text-sm text-red-600">
            <AlertCircle className="mr-1 w-4 h-4" />
            {validationErrors.description}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold text-gray-700">
          Short Description <span className="text-red-500">*</span>
          <span className="ml-2 text-xs text-gray-500">
            (appears in policy cards)
          </span>
        </label>
        <input
          type="text"
          value={formData.shortDescription}
          onChange={(e) => updateFormData("shortDescription", e.target.value)}
          placeholder="Brief one-liner about the policy"
          maxLength={100}
          className="px-4 py-3 w-full rounded-xl border border-gray-300 transition-all duration-200 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <div className="text-xs text-right text-gray-500">
          {formData.shortDescription.length}/100 characters
        </div>
        {validationErrors.shortDescription && (
          <p className="flex items-center text-sm text-red-600">
            <AlertCircle className="mr-1 w-4 h-4" />
            {validationErrors.shortDescription}
          </p>
        )}
      </div> */}
    </motion.div>
  );
}

// =========================
// STEP 2: Coverage (Dynamic based on policy type)
// =========================
export function StepCoverage({ formData, updateFormData, validationErrors }) {
  const isTripSecure = formData.insuranceType === "tripsecure";
  const isTravel = formData.insuranceType === "travel";
  const isLife = formData.insuranceType === "life";
  const isGeneral = formData.insuranceType === "general";
  const isIntercity = formData.policyType === "intercity_travel";
  const isMotoRider = formData.policyType === "okada_rider";
  const isDailyCommuter = formData.policyType === "daily_commuter";
  const isFixedCommuter = isMotoRider || isDailyCommuter;

  // Drawer state for package editing
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [editingPackageIndex, setEditingPackageIndex] = useState(-1);

  // Package management for Life, General, and Travel Insurance
  const addPackage = () => {
    const defaultDurationUnit =
      formData.insuranceType === "travel" ? "hours" : "years";
    const newPackage = {
      id: Date.now() + Math.random(),
      productName: "",
      premium: "",
      premiumDuration: "monthly", // Default premium payment term
      durationValue: "",
      durationUnit: defaultDurationUnit,
      description: "",
      max_claims: 3, // Per-package max claims (configurable per package)
      is_quote_only: false,
      quote_request_fields: [], // [{ key, label, type, required, options? }]
      // Per-package policy fields
      insurance_coverage_list: [],
      exclusions: [],
      eligibility_criteria: [],
      claim_process_steps: [],
      terms: "",
      termsInputType: "text",
    };
    // Open drawer with new package
    setEditingPackage(newPackage);
    setEditingPackageIndex((formData.packages || []).length);
    setIsDrawerOpen(true);
  };

  const editPackage = (pkg) => {
    const index = (formData.packages || []).findIndex((p) => p.id === pkg.id);
    setEditingPackage(pkg);
    setEditingPackageIndex(index);
    setIsDrawerOpen(true);
  };

  const savePackage = (editedPackage) => {
    const packages = formData.packages || [];
    if (editingPackageIndex >= 0 && editingPackageIndex < packages.length) {
      // Update existing package
      const updated = [...packages];
      updated[editingPackageIndex] = editedPackage;
      updateFormData("packages", updated);
    } else {
      // Add new package
      updateFormData("packages", [...packages, editedPackage]);
    }
    setIsDrawerOpen(false);
    setEditingPackage(null);
    setEditingPackageIndex(-1);
  };

  const removePackage = (id) => {
    const filtered = (formData.packages || []).filter((pkg) => pkg.id !== id);
    updateFormData("packages", filtered);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setEditingPackage(null);
    setEditingPackageIndex(-1);
  };

  // Life, General, or Travel Insurance - Packages
  if (isLife || isGeneral || isTravel) {
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="space-y-8"
      >
        <div
          className={`p-4 rounded-xl border ${
            isLife
              ? "bg-green-50 border-green-200"
              : isGeneral
              ? "bg-purple-50 border-purple-200"
              : "bg-indigo-50 border-indigo-200"
          }`}
        >
          <div className="flex items-start space-x-3">
            <Info
              className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                isLife
                  ? "text-green-600"
                  : isGeneral
                  ? "text-purple-600"
                  : "text-indigo-600"
              }`}
            />
            <div>
              <h4
                className={`mb-1 font-semibold ${
                  isLife
                    ? "text-green-900"
                    : isGeneral
                    ? "text-purple-900"
                    : "text-indigo-900"
                }`}
              >
                {isLife
                  ? "Life Insurance"
                  : isGeneral
                  ? "General Insurance"
                  : "Travel Insurance"}{" "}
                Package Configuration
              </h4>
              <p
                className={`text-sm ${
                  isLife
                    ? "text-green-700"
                    : isGeneral
                    ? "text-purple-700"
                    : "text-indigo-700"
                }`}
              >
                Add packages that fall under this{" "}
                {isLife
                  ? "life insurance"
                  : isGeneral
                  ? "general insurance"
                  : "travel insurance"}{" "}
                policy. Each package must have a product name, premium, duration
                (hours, weekly, monthly, or yearly for travel), and description.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-semibold text-gray-700">
              Packages <span className="text-red-500">*</span>
            </label>
            <button
              onClick={addPackage}
              className={`flex items-center px-4 py-2 text-white rounded-lg transition-colors ${
                isLife
                  ? "bg-green-600 hover:bg-green-700"
                  : isGeneral
                  ? "bg-purple-600 hover:bg-purple-700"
                  : "bg-indigo-600 hover:bg-indigo-700"
              }`}
            >
              <Plus className="mr-2 w-4 h-4" />
              Add Package
            </button>
          </div>

          {!formData.packages || formData.packages.length === 0 ? (
            <div
              className={`py-12 text-center rounded-xl border-2 border-dashed ${
                isLife
                  ? "bg-green-50 border-green-200"
                  : isGeneral
                  ? "bg-purple-50 border-purple-200"
                  : "bg-indigo-50 border-indigo-200"
              }`}
            >
              <Package
                className={`mx-auto mb-4 w-16 h-16 ${
                  isLife
                    ? "text-green-400"
                    : isGeneral
                    ? "text-purple-400"
                    : "text-indigo-400"
                }`}
              />
              <p className="mb-4 text-lg font-semibold text-gray-700">No packages added yet</p>
              <p className="mb-6 text-sm text-gray-500">
                Click the button below to create your first package
              </p>
              <button
                onClick={addPackage}
                className={`inline-flex items-center px-6 py-3 text-white rounded-lg shadow-lg transition-all hover:shadow-xl ${
                  isLife
                    ? "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                    : isGeneral
                    ? "bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700"
                    : "bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
                }`}
              >
                <Plus className="mr-2 w-5 h-5" />
                Add Your First Package
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <AnimatePresence>
                {(formData.packages || []).map((pkg, index) => (
                  <PackagePreviewCard
                    key={pkg.id}
                    package={pkg}
                    index={index}
                    onEdit={editPackage}
                    onRemove={removePackage}
                    insuranceType={formData.insuranceType}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}

          {/* Package Drawer */}
          <PackageDrawer
            isOpen={isDrawerOpen}
            onClose={closeDrawer}
            package={editingPackage}
            index={editingPackageIndex}
            onSave={savePackage}
            insuranceType={formData.insuranceType}
          />

          {validationErrors.packages && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center px-4 py-3 text-sm text-red-600 bg-red-50 rounded-lg"
            >
              <AlertCircle className="flex-shrink-0 mr-2 w-5 h-5" />
              {validationErrors.packages}
            </motion.p>
          )}
        </div>
      </motion.div>
    );
  }

  // TripSecure Insurance - Intercity Travel Coverage
  if (isTripSecure && formData.policyType && isIntercity) {
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="space-y-8"
      >
        <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="mb-1 font-semibold text-blue-900">
                Intercity Travel Configuration
              </h4>
              <p className="text-sm text-blue-700">
                Select your coverage area and configure routes. You can charge
                less than the maximum price, but not more.
              </p>
            </div>
          </div>
        </div>

        {/* Geographic Coverage Type */}
        <div className="space-y-4">
          <label className="block text-sm font-semibold text-gray-700">
            Geographic Coverage <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {["regional", "international", "both"].map((type) => {
              const isSelected = formData.geography === type;
              return (
                <motion.div
                  key={type}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => updateFormData("geography", type)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    isSelected
                      ? "bg-blue-50 border-blue-500 shadow-md"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="text-center">
                    <div
                      className={`inline-flex items-center justify-center w-12 h-12 rounded-full mb-2 ${
                        isSelected ? "bg-blue-100" : "bg-gray-100"
                      }`}
                    >
                      <Globe
                        className={`w-6 h-6 ${
                          isSelected ? "text-blue-600" : "text-gray-500"
                        }`}
                      />
                    </div>
                    <h3 className="font-semibold text-gray-900 capitalize">
                      {type}
                    </h3>
                    <p className="mt-1 text-xs text-gray-600">
                      {type === "regional" && "Cover local regional routes"}
                      {type === "international" && "Cover international routes"}
                      {type === "both" && "Cover all routes"}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
          {validationErrors.geography && (
            <p className="flex items-center text-sm text-red-600">
              <AlertCircle className="mr-1 w-4 h-4" />
              {validationErrors.geography}
            </p>
          )}
        </div>

        {/* Regional Routes */}
        {(formData.geography === "regional" ||
          formData.geography === "both") && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">
                Regional Routes
              </h3>
              <span className="text-sm text-gray-600">
                Max Price: ₵10 per route
              </span>
            </div>
            <div className="grid grid-cols-1 gap-4 items-start md:grid-cols-2">
              {REGIONAL_ROUTES.map((route) => {
                const routeData = formData.regionalRoutes?.[
                  route.destination
                ] || { selected: false, price: "" };
                return (
                  <RouteSelectionCard
                    key={route.destination}
                    route={route}
                    isSelected={routeData.selected}
                    price={routeData.price}
                    onToggle={() => {
                      const newRoutes = { ...formData.regionalRoutes };
                      if (!newRoutes[route.destination]) {
                        newRoutes[route.destination] = {
                          selected: true,
                          price: route.maxPrice.toString(),
                        };
                      } else {
                        const isCurrentlySelected =
                          newRoutes[route.destination].selected;
                        if (isCurrentlySelected) {
                          // Unchecking: clear the price and set selected to false
                          newRoutes[route.destination] = {
                            selected: false,
                            price: "",
                          };
                        } else {
                          // Checking: set to maximum price
                          newRoutes[route.destination] = {
                            selected: true,
                            price: route.maxPrice.toString(),
                          };
                        }
                      }
                      updateFormData("regionalRoutes", newRoutes);
                    }}
                    onPriceChange={(value) => {
                      const newRoutes = { ...formData.regionalRoutes };
                      newRoutes[route.destination] = {
                        ...newRoutes[route.destination],
                        price: value,
                      };
                      updateFormData("regionalRoutes", newRoutes);
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* International Routes */}
        {(formData.geography === "international" ||
          formData.geography === "both") && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">
                International Routes
              </h3>
              <span className="text-sm text-gray-600">
                Max Price: ₵20 per route
              </span>
            </div>
            <div className="grid grid-cols-1 gap-4 items-start md:grid-cols-2">
              {INTERNATIONAL_ROUTES.map((route) => {
                const routeData = formData.internationalRoutes?.[
                  route.destination
                ] || { selected: false, price: "" };
                return (
                  <RouteSelectionCard
                    key={route.destination}
                    route={route}
                    isSelected={routeData.selected}
                    price={routeData.price}
                    onToggle={() => {
                      const newRoutes = { ...formData.internationalRoutes };
                      if (!newRoutes[route.destination]) {
                        newRoutes[route.destination] = {
                          selected: true,
                          price: route.maxPrice.toString(),
                        };
                      } else {
                        const isCurrentlySelected =
                          newRoutes[route.destination].selected;
                        if (isCurrentlySelected) {
                          // Unchecking: clear the price and set selected to false
                          newRoutes[route.destination] = {
                            selected: false,
                            price: "",
                          };
                        } else {
                          // Checking: set to maximum price
                          newRoutes[route.destination] = {
                            selected: true,
                            price: route.maxPrice.toString(),
                          };
                        }
                      }
                      updateFormData("internationalRoutes", newRoutes);
                    }}
                    onPriceChange={(value) => {
                      const newRoutes = { ...formData.internationalRoutes };
                      newRoutes[route.destination] = {
                        ...newRoutes[route.destination],
                        price: value,
                      };
                      updateFormData("internationalRoutes", newRoutes);
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}

        {validationErrors.routes && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center px-4 py-3 text-sm text-red-600 bg-red-50 rounded-lg"
          >
            <AlertCircle className="flex-shrink-0 mr-2 w-5 h-5" />
            {validationErrors.routes}
          </motion.p>
        )}
      </motion.div>
    );
  }

  // TripSecure Insurance - Motor Rider & Daily Commuter Coverage
  if (isTripSecure && formData.policyType && isFixedCommuter) {
    const productName = isDailyCommuter ? "Daily Commuter" : "Motor Rider";
    const accent = isDailyCommuter
      ? {
          panel: "bg-blue-50 border-blue-200",
          title: "text-blue-900",
          body: "text-blue-700",
          icon: "text-blue-600",
          card: "border-blue-200",
          focus: "focus:ring-blue-500 focus:border-blue-500",
          hint: "text-blue-500",
        }
      : {
          panel: "bg-orange-50 border-orange-200",
          title: "text-orange-900",
          body: "text-orange-700",
          icon: "text-orange-600",
          card: "border-orange-200",
          focus: "focus:ring-orange-500 focus:border-orange-500",
          hint: "text-orange-500",
        };

    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="space-y-8"
      >
        <div className={`p-6 rounded-xl border ${accent.panel}`}>
          <div className="flex items-start mb-4 space-x-3">
            <Info className={`w-5 h-5 mt-0.5 flex-shrink-0 ${accent.icon}`} />
            <div>
              <h4 className={`mb-1 font-semibold ${accent.title}`}>
                {productName} coverage pricing
              </h4>
              <p className={`text-sm ${accent.body}`}>
                Duration options are fixed by TripSecure. You may charge at or
                below the maximum for each plan, but not above it.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 mt-4 md:grid-cols-2">
            {FIXED_COMMUTER_COVERAGES.map((tier) => {
              const price = formData[tier.key] || "";
              const numeric = parseFloat(price);
              const isTooHigh = price && numeric > tier.maxPrice;
              const isTooLow = price && numeric < 0.01;

              return (
                <div
                  key={tier.key}
                  className={`p-4 bg-white rounded-lg border ${accent.card}`}
                >
                  <div className="flex items-center mb-3 space-x-2">
                    <Calendar className={`w-5 h-5 ${accent.icon}`} />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Duration</p>
                      <p className="text-xl font-bold text-gray-900">{tier.label}</p>
                    </div>
                  </div>
                  <p className="mb-3 text-xs text-gray-500">
                    Maximum price ₵{tier.maxPrice}. You can charge less.
                  </p>
                  <label className="block mb-2 text-sm font-semibold text-gray-700">
                    Your price <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 text-lg font-bold text-gray-500 -translate-y-1/2">
                      ₵
                    </span>
                    <input
                      type="number"
                      min="0.01"
                      max={tier.maxPrice}
                      step="0.01"
                      value={price}
                      onChange={(e) => updateFormData(tier.key, e.target.value)}
                      placeholder={`Max ${tier.maxPrice.toFixed(2)}`}
                      className={`py-3 pr-4 pl-10 w-full text-lg font-semibold rounded-xl border transition-all duration-200 ${
                        isTooHigh
                          ? "border-red-500 focus:ring-2 focus:ring-red-500 bg-red-50"
                          : isTooLow
                          ? "border-yellow-500 focus:ring-2 focus:ring-yellow-500 bg-yellow-50"
                          : `border-gray-300 focus:ring-2 ${accent.focus}`
                      }`}
                    />
                  </div>
                  {isTooHigh && (
                    <p className="flex items-start mt-2 text-xs font-medium text-red-600">
                      <AlertCircle className="mr-1 w-4 h-4 mt-0.5 shrink-0" />
                      {tier.label} cannot exceed ₵{tier.maxPrice}.
                    </p>
                  )}
                  {isTooLow && (
                    <p className="flex items-start mt-2 text-xs font-medium text-yellow-600">
                      <AlertCircle className="mr-1 w-4 h-4 mt-0.5 shrink-0" />
                      Minimum amount is ₵0.01.
                    </p>
                  )}
                  {validationErrors[tier.key] && (
                    <p className="flex items-center mt-2 text-sm text-red-600">
                      <AlertCircle className="mr-1 w-4 h-4" />
                      {validationErrors[tier.key]}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>
    );
  }

  // Default/No insurance type or policy type selected
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex items-center justify-center min-h-[400px]"
    >
      <div className="max-w-md text-center">
        <Shield className="mx-auto mb-4 w-16 h-16 text-gray-400" />
        <h3 className="mb-2 text-xl font-semibold text-gray-900">
          {!isTripSecure
            ? "Select Insurance Type First"
            : "Select a Policy Type First"}
        </h3>
        <p className="text-gray-600">
          {!isTripSecure
            ? "Please go back and select an insurance type to configure coverage options"
            : "Please go back and select a policy type to configure coverage options"}
        </p>
      </div>
    </motion.div>
  );
}

// =========================
// STEP 3: Benefits & Exclusions (Replaces Risk Assessment)
// =========================
export function StepBenefitsExclusions({
  formData,
  updateFormData,
  validationErrors,
}) {
  const isTripSecure = formData.insuranceType === "tripsecure";
  const isIntercity = formData.policyType === "intercity_travel";
  const hasBothCoverage = formData.geography === "both";

  // Helper function to get coverage period from packages with package names
  const getCoveragePeriodFromPackages = () => {
    if (!formData.packages || formData.packages.length === 0) {
      return "";
    }
    const periods = formData.packages
      .filter((pkg) => pkg.durationValue && pkg.durationUnit)
      .map((pkg) => {
        const value = pkg.durationValue;
        const unit = pkg.durationUnit;
        const packageName = pkg.productName?.trim() || "Package";
        // Format unit for display
        let displayUnit = unit;
        if (unit === "hours" || unit === "hour") displayUnit = "hours";
        else if (unit === "days" || unit === "day") displayUnit = "days";
        else if (unit === "weeks" || unit === "week") displayUnit = "weeks";
        else if (unit === "months" || unit === "month") displayUnit = "months";
        else if (unit === "years") displayUnit = "years";
        // Legacy support
        else if (unit === "weekly") displayUnit = "weeks";
        else if (unit === "monthly") displayUnit = "months";
        else if (unit === "yearly") displayUnit = "years";
        return `${packageName}: ${value} ${displayUnit}`;
      });
    return periods.length > 0
      ? periods.join(", ") + " (from packages)"
      : "";
  };

  // Helper function to get exclusions text from exclusionsList
  const getExclusionsFromList = () => {
    if (!formData.exclusionsList || formData.exclusionsList.length === 0) {
      return "";
    }
    const exclusionTexts = formData.exclusionsList
      .filter((ex) => ex.text && ex.text.trim() !== "")
      .map((ex) => ex.text.trim());
    return exclusionTexts.length > 0
      ? exclusionTexts.join("; ") + " (from exclusions)"
      : "";
  };

  // Helper function to get claims process from claimProcessSteps
  const getClaimsProcessFromSteps = () => {
    if (!formData.claimProcessSteps || formData.claimProcessSteps.length === 0) {
      return "";
    }
    const validSteps = formData.claimProcessSteps.filter(
      (step) =>
        step.title?.trim() &&
        step.description?.trim() &&
        step.timeframe?.trim()
    );
    if (validSteps.length === 0) {
      return "";
    }
    const processText = validSteps
      .map((step, idx) => {
        let text = `${idx + 1}. ${step.title}: ${step.description}`;
        if (step.timeframe) {
          text += ` (Timeframe: ${step.timeframe})`;
        }
        if (step.requirements) {
          text += ` Requirements: ${step.requirements}`;
        }
        return text;
      })
      .join("\n");
    return processText + " (from claim process steps)";
  };

  // Auto-populate template fields when switching to template mode or when source data changes
  useEffect(() => {
    if (formData.termsInputType === "template") {
      const currentTemplate = formData.termsTemplateData || {};
      const updatedTemplate = { ...currentTemplate };
      let hasChanges = false;

      // Auto-populate coverage period if empty
      const coveragePeriod = getCoveragePeriodFromPackages().replace(" (from packages)", "");
      if (coveragePeriod && (!currentTemplate.coveragePeriod || currentTemplate.coveragePeriod.trim() === "")) {
        updatedTemplate.coveragePeriod = coveragePeriod;
        hasChanges = true;
      }

      // Auto-populate exclusions if empty
      const exclusions = getExclusionsFromList().replace(" (from exclusions)", "");
      if (exclusions && (!currentTemplate.exclusions || currentTemplate.exclusions.trim() === "")) {
        updatedTemplate.exclusions = exclusions;
        hasChanges = true;
      }

      // Auto-populate claims process if empty
      const claimsProcess = getClaimsProcessFromSteps().replace(" (from claim process steps)", "");
      if (claimsProcess && (!currentTemplate.claimsProcess || currentTemplate.claimsProcess.trim() === "")) {
        updatedTemplate.claimsProcess = claimsProcess;
        hasChanges = true;
      }

      // Only update if there are changes
      if (hasChanges) {
        updateFormData("termsTemplateData", updatedTemplate);
      }
    }
  }, [
    formData.termsInputType,
    formData.packages,
    formData.exclusionsList,
    formData.claimProcessSteps,
  ]);

  // Add benefit
  const addBenefit = (type) => {
    // If no type specified, determine based on current geography
    let benefitType = type;
    if (!type) {
      if (formData.geography === "regional") {
        benefitType = "regional";
      } else if (formData.geography === "international") {
        benefitType = "international";
      } else {
        benefitType = "general";
      }
    }

    const newBenefit = {
      id: Date.now() + Math.random(),
      title: "",
      description: "",
      limit: "",
      type: benefitType, // 'general', 'regional', 'international'
    };
    updateFormData("insuranceCoverageList", [
      ...formData.insuranceCoverageList,
      newBenefit,
    ]);
  };

  // Update benefit
  const updateBenefit = (id, field, value) => {
    const updated = formData.insuranceCoverageList.map((item) =>
      item.id === id ? { ...item, [field]: value } : item
    );
    updateFormData("insuranceCoverageList", updated);
  };

  // Remove benefit
  const removeBenefit = (id) => {
    const filtered = formData.insuranceCoverageList.filter(
      (item) => item.id !== id
    );
    updateFormData("insuranceCoverageList", filtered);
  };

  // Add exclusion
  const addExclusion = (type) => {
    // If no type specified, determine based on current geography
    let exclusionType = type;
    if (!type) {
      if (formData.geography === "regional") {
        exclusionType = "regional";
      } else if (formData.geography === "international") {
        exclusionType = "international";
      } else {
        exclusionType = "general";
      }
    }

    const newExclusion = {
      id: Date.now() + Math.random(),
      text: "",
      type: exclusionType, // 'general', 'regional', 'international'
    };
    updateFormData("exclusionsList", [
      ...(formData.exclusionsList || []),
      newExclusion,
    ]);
  };

  // Update exclusion
  const updateExclusion = (id, field, value) => {
    const updated = (formData.exclusionsList || []).map((item) =>
      item.id === id ? { ...item, [field]: value } : item
    );
    updateFormData("exclusionsList", updated);
  };

  // Remove exclusion
  const removeExclusion = (id) => {
    const filtered = (formData.exclusionsList || []).filter(
      (item) => item.id !== id
    );
    updateFormData("exclusionsList", filtered);
  };

  // Filter benefits and exclusions by type
  const getBenefitsByType = (type) => {
    const benefits = formData.insuranceCoverageList || [];
    console.log(`Filtering benefits for type "${type}":`, benefits);
    const filtered = benefits.filter((b) => {
      if (!b.type || b.type === undefined || b.type === "general") {
        if (type === "general") {
          return true;
        }
        return type === "regional";
      }
      return b.type === type;
    });
    console.log(`Filtered benefits for "${type}":`, filtered);
    return filtered;
  };

  const getExclusionsByType = (type) => {
    const exclusions = formData.exclusionsList || [];
    return exclusions.filter((e) => {
      if (!e.type || e.type === undefined || e.type === "general") {
        if (type === "general") {
          return true;
        }
        return type === "regional";
      }
      return e.type === type;
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <div className="p-4 bg-green-50 rounded-xl border border-green-200">
        <div className="flex items-start space-x-3">
          <Info className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="mb-1 font-semibold text-green-900">
              Define Benefits & Exclusions
            </h4>
            <p className="text-sm text-green-700">
              {isIntercity && hasBothCoverage
                ? "Since you selected 'Both' coverage, you can define separate benefits and exclusions for Regional and International routes."
                : "Define what your policy covers (benefits) and what it doesn't cover (exclusions)."}
            </p>
          </div>
        </div>
      </div>

      {/* For TripSecure Intercity with Both coverage - Separate sections */}
      {isTripSecure && isIntercity && hasBothCoverage ? (
        <>
          {/* Regional Benefits & Exclusions */}
          <div className="p-6 bg-blue-50 rounded-xl border border-blue-200">
            <h3 className="flex items-center mb-4 text-lg font-bold text-blue-900">
              <MapPin className="mr-2 w-5 h-5" />
              Regional Coverage
            </h3>

            {/* Regional Benefits */}
            <div className="mb-6 space-y-4">
              <label className="block text-sm font-semibold text-gray-700">
                Regional Benefits <span className="text-red-500">*</span>
              </label>

              {getBenefitsByType("regional").length === 0 ? (
                <div className="py-8 text-center bg-white rounded-lg border-2 border-blue-200 border-dashed">
                  <Shield className="mx-auto mb-2 w-12 h-12 text-blue-300" />
                  <p className="text-sm text-gray-500">
                    No regional benefits added yet
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {getBenefitsByType("regional").map((benefit, index) => (
                    <InsuranceCoverageItem
                      key={benefit.id}
                      index={index}
                      item={benefit}
                      onChangeField={updateBenefit}
                      onRemove={removeBenefit}
                      title="Regional Benefit"
                    />
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => addBenefit("regional")}
                className="flex justify-center items-center px-4 py-2.5 w-full text-sm font-medium text-blue-700 bg-blue-50 rounded-lg border border-blue-200 transition-colors hover:bg-blue-100"
              >
                <Plus className="mr-2 w-4 h-4" />
                Add Benefit
              </button>
            </div>

            {/* Regional Exclusions */}
            <div className="space-y-4">
              <label className="block text-sm font-semibold text-gray-700">
                Regional Exclusions
              </label>

              {getExclusionsByType("regional").length === 0 ? (
                <div className="py-8 text-center bg-white rounded-lg border-2 border-red-200 border-dashed">
                  <AlertCircle className="mx-auto mb-2 w-12 h-12 text-red-300" />
                  <p className="text-sm text-gray-500">
                    No regional exclusions added yet
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {getExclusionsByType("regional").map((exclusion, index) => (
                    <ExclusionItem
                      key={exclusion.id}
                      index={index}
                      item={exclusion}
                      onChangeField={updateExclusion}
                      onRemove={removeExclusion}
                    />
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => addExclusion("regional")}
                className="flex justify-center items-center px-4 py-2.5 w-full text-sm font-medium text-red-700 bg-red-50 rounded-lg border border-red-200 transition-colors hover:bg-red-100"
              >
                <Plus className="mr-2 w-4 h-4" />
                Add Exclusion
              </button>
            </div>
          </div>

          {/* International Benefits & Exclusions */}
          <div className="p-6 bg-indigo-50 rounded-xl border border-indigo-200">
            <h3 className="flex items-center mb-4 text-lg font-bold text-indigo-900">
              <Globe className="mr-2 w-5 h-5" />
              International Coverage
            </h3>

            {/* International Benefits */}
            <div className="mb-6 space-y-4">
              <label className="block text-sm font-semibold text-gray-700">
                International Benefits <span className="text-red-500">*</span>
              </label>

              {getBenefitsByType("international").length === 0 ? (
                <div className="py-8 text-center bg-white rounded-lg border-2 border-indigo-200 border-dashed">
                  <Shield className="mx-auto mb-2 w-12 h-12 text-indigo-300" />
                  <p className="text-sm text-gray-500">
                    No international benefits added yet
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {getBenefitsByType("international").map((benefit, index) => (
                    <InsuranceCoverageItem
                      key={benefit.id}
                      index={index}
                      item={benefit}
                      onChangeField={updateBenefit}
                      onRemove={removeBenefit}
                      title="International Benefit"
                    />
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => addBenefit("international")}
                className="flex justify-center items-center px-4 py-2.5 w-full text-sm font-medium text-indigo-700 bg-indigo-50 rounded-lg border border-indigo-200 transition-colors hover:bg-indigo-100"
              >
                <Plus className="mr-2 w-4 h-4" />
                Add Benefit
              </button>
            </div>

            {/* International Exclusions */}
            <div className="space-y-4">
              <label className="block text-sm font-semibold text-gray-700">
                International Exclusions
              </label>

              {getExclusionsByType("international").length === 0 ? (
                <div className="py-8 text-center bg-white rounded-lg border-2 border-red-200 border-dashed">
                  <AlertCircle className="mx-auto mb-2 w-12 h-12 text-red-300" />
                  <p className="text-sm text-gray-500">
                    No international exclusions added yet
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {getExclusionsByType("international").map(
                    (exclusion, index) => (
                      <ExclusionItem
                        key={exclusion.id}
                        index={index}
                        item={exclusion}
                        onChangeField={updateExclusion}
                        onRemove={removeExclusion}
                      />
                    )
                  )}
                </div>
              )}
              <button
                type="button"
                onClick={() => addExclusion("international")}
                className="flex justify-center items-center px-4 py-2.5 w-full text-sm font-medium text-red-700 bg-red-50 rounded-lg border border-red-200 transition-colors hover:bg-red-100"
              >
                <Plus className="mr-2 w-4 h-4" />
                Add Exclusion
              </button>
            </div>
          </div>
        </>
      ) : (
        // Single set of Benefits & Exclusions for all other cases
        <>
          {/* General Benefits */}
          <div className="space-y-4">
            <label className="block text-sm font-semibold text-gray-700">
              Coverage Benefits <span className="text-red-500">*</span>
            </label>

            {getBenefitsByType("general").length === 0 ? (
              <div className="py-12 text-center bg-gray-50 rounded-xl border-2 border-gray-300 border-dashed">
                <Shield className="mx-auto mb-4 w-16 h-16 text-gray-400" />
                <p className="text-gray-500">No benefits added yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {getBenefitsByType("general").map((benefit, index) => (
                  <InsuranceCoverageItem
                    key={benefit.id}
                    index={index}
                    item={benefit}
                    onChangeField={updateBenefit}
                    onRemove={removeBenefit}
                  />
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => addBenefit("general")}
              className="flex justify-center items-center px-4 py-2.5 w-full text-sm font-medium text-green-700 bg-green-50 rounded-lg border border-green-200 transition-colors hover:bg-green-100"
            >
              <Plus className="mr-2 w-4 h-4" />
              Add Benefit
            </button>

            {validationErrors.benefits && (
              <p className="flex items-center text-sm text-red-600">
                <AlertCircle className="mr-1 w-4 h-4" />
                {validationErrors.benefits}
              </p>
            )}
          </div>

          {/* General Exclusions */}
          <div className="space-y-4">
            <label className="block text-sm font-semibold text-gray-700">
              Policy Exclusions
            </label>

            {getExclusionsByType("general").length === 0 ? (
              <div className="py-12 text-center bg-gray-50 rounded-xl border-2 border-gray-300 border-dashed">
                <AlertCircle className="mx-auto mb-4 w-16 h-16 text-gray-400" />
                <p className="text-gray-500">No exclusions added yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {getExclusionsByType("general").map((exclusion, index) => (
                  <ExclusionItem
                    key={exclusion.id}
                    index={index}
                    item={exclusion}
                    onChangeField={updateExclusion}
                    onRemove={removeExclusion}
                  />
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => addExclusion("general")}
              className="flex justify-center items-center px-4 py-2.5 w-full text-sm font-medium text-red-700 bg-red-50 rounded-lg border border-red-200 transition-colors hover:bg-red-100"
            >
              <Plus className="mr-2 w-4 h-4" />
              Add Exclusion
            </button>
          </div>
        </>
      )}

      {validationErrors.benefitsExclusions && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center px-4 py-3 text-sm text-red-600 bg-red-50 rounded-lg"
        >
          <AlertCircle className="flex-shrink-0 mr-2 w-5 h-5" />
          {validationErrors.benefitsExclusions}
        </motion.p>
      )}

      {/* Provider-managed fields for Travel, Life, and General Insurance */}
      {(formData.insuranceType === "travel" ||
        formData.insuranceType === "life" ||
        formData.insuranceType === "general") && (
        <>
          <div className="pt-6 mt-6 border-t border-gray-300">
            <div className="p-4 mb-6 bg-blue-50 rounded-xl border border-blue-200">
              <div className="flex items-start space-x-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="mb-1 font-semibold text-blue-900">
                    Policy Details Management
                  </h4>
                  <p className="text-sm text-blue-700">
                    As a provider, you can define and manage the following
                    details for your policy.
                  </p>
                </div>
              </div>
            </div>

            {/* Eligibility Criteria */}
            <div className="mb-6 space-y-4">
              <div className="flex justify-between items-center">
                <label className="block text-sm font-semibold text-gray-700">
                  Eligibility Criteria <span className="text-red-500">*</span>
                </label>
                <button
                  onClick={() => {
                    const newCriteria = [
                      ...(formData.eligibilityCriteria || []),
                      "",
                    ];
                    updateFormData("eligibilityCriteria", newCriteria);
                  }}
                  className="flex items-center px-3 py-2 text-sm text-blue-600 bg-blue-100 rounded-lg transition-colors hover:bg-blue-200"
                >
                  <Plus className="mr-1 w-4 h-4" />
                  Add Criterion
                </button>
              </div>

              {(!formData.eligibilityCriteria ||
                formData.eligibilityCriteria.length === 0) ? (
                <div className="py-8 text-center bg-gray-50 rounded-lg border-2 border-gray-300 border-dashed">
                  <CheckCircle className="mx-auto mb-2 w-12 h-12 text-gray-400" />
                  <p className="mb-4 text-sm text-gray-500">
                    No eligibility criteria added yet
                  </p>
                  <button
                    onClick={() => {
                      updateFormData("eligibilityCriteria", [""]);
                    }}
                    className="inline-flex items-center px-4 py-2 text-sm text-blue-600 bg-blue-100 rounded-lg transition-colors hover:bg-blue-200"
                  >
                    <Plus className="mr-2 w-4 h-4" />
                    Add Your First Criterion
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.eligibilityCriteria.map((criterion, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-start space-x-3"
                    >
                      <div className="flex-1">
                        <input
                          type="text"
                          value={criterion}
                          onChange={(e) => {
                            const updated = [...formData.eligibilityCriteria];
                            updated[index] = e.target.value;
                            updateFormData("eligibilityCriteria", updated);
                          }}
                          placeholder="e.g., Must be 18 years or older"
                          className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <button
                        onClick={() => {
                          const updated = formData.eligibilityCriteria.filter(
                            (_, i) => i !== index
                          );
                          updateFormData("eligibilityCriteria", updated);
                        }}
                        className="p-2 text-red-600 rounded-lg transition-colors hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </motion.div>
                  ))}
                </div>
              )}
              {validationErrors.eligibilityCriteria && (
                <p className="flex items-center mt-2 text-sm text-red-600">
                  <AlertCircle className="mr-1 w-4 h-4" />
                  {validationErrors.eligibilityCriteria}
                </p>
              )}
            </div>

            {/* Claim Process Steps */}
            <div className="mb-6 space-y-4">
              <div className="flex justify-between items-center">
                <label className="block text-sm font-semibold text-gray-700">
                  Claim Process Steps <span className="text-red-500">*</span>
                </label>
                <button
                  onClick={() => {
                    const newSteps = [
                      ...(formData.claimProcessSteps || []),
                      {
                        step_number: (formData.claimProcessSteps?.length || 0) + 1,
                        title: "",
                        description: "",
                        timeframe: "",
                        requirements: "",
                      },
                    ];
                    updateFormData("claimProcessSteps", newSteps);
                  }}
                  className="flex items-center px-3 py-2 text-sm text-blue-600 bg-blue-100 rounded-lg transition-colors hover:bg-blue-200"
                >
                  <Plus className="mr-1 w-4 h-4" />
                  Add Step
                </button>
              </div>

              {(!formData.claimProcessSteps ||
                formData.claimProcessSteps.length === 0) ? (
                <div className="py-8 text-center bg-gray-50 rounded-lg border-2 border-gray-300 border-dashed">
                  <Clock className="mx-auto mb-2 w-12 h-12 text-gray-400" />
                  <p className="mb-4 text-sm text-gray-500">
                    No claim process steps added yet
                  </p>
                  <button
                    onClick={() => {
                      updateFormData("claimProcessSteps", [
                        {
                          step_number: 1,
                          title: "",
                          description: "",
                          timeframe: "",
                          requirements: "",
                        },
                      ]);
                    }}
                    className="inline-flex items-center px-4 py-2 text-sm text-blue-600 bg-blue-100 rounded-lg transition-colors hover:bg-blue-200"
                  >
                    <Plus className="mr-2 w-4 h-4" />
                    Add Your First Step
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.claimProcessSteps.map((step, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 bg-white rounded-lg border border-gray-200"
                    >
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-medium text-gray-700">
                          Step {step.step_number || index + 1}
                        </span>
                        <button
                          onClick={() => {
                            const updated = formData.claimProcessSteps
                              .filter((_, i) => i !== index)
                              .map((s, idx) => ({
                                ...s,
                                step_number: idx + 1,
                              }));
                            updateFormData("claimProcessSteps", updated);
                          }}
                          className="p-1 text-red-600 rounded-lg transition-colors hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <label className="block mb-1 text-xs font-medium text-gray-600">
                            Step Title <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={step.title || ""}
                            onChange={(e) => {
                              const updated = [...formData.claimProcessSteps];
                              updated[index] = { ...updated[index], title: e.target.value };
                              updateFormData("claimProcessSteps", updated);
                            }}
                            placeholder="e.g., Report Incident"
                            className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block mb-1 text-xs font-medium text-gray-600">
                            Timeframe <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={step.timeframe || ""}
                            onChange={(e) => {
                              const updated = [...formData.claimProcessSteps];
                              updated[index] = { ...updated[index], timeframe: e.target.value };
                              updateFormData("claimProcessSteps", updated);
                            }}
                            placeholder="e.g., Within 24 hours"
                            className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="mt-3">
                        <label className="block mb-1 text-xs font-medium text-gray-600">
                          Description <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={step.description || ""}
                          onChange={(e) => {
                            const updated = [...formData.claimProcessSteps];
                            updated[index] = { ...updated[index], description: e.target.value };
                            updateFormData("claimProcessSteps", updated);
                          }}
                          placeholder="Describe what happens in this step..."
                          rows={2}
                          className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="mt-3">
                        <label className="block mb-1 text-xs font-medium text-gray-600">
                          Requirements
                        </label>
                        <input
                          type="text"
                          value={step.requirements || ""}
                          onChange={(e) => {
                            const updated = [...formData.claimProcessSteps];
                            updated[index] = { ...updated[index], requirements: e.target.value };
                            updateFormData("claimProcessSteps", updated);
                          }}
                          placeholder="e.g., Incident details and location"
                          className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
              {validationErrors.claimProcessSteps && (
                <p className="flex items-center mt-2 text-sm text-red-600">
                  <AlertCircle className="mr-1 w-4 h-4" />
                  {validationErrors.claimProcessSteps}
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
}

// =========================
// STEP 5: Terms and Conditions
// =========================
export function StepTermsAndConditions({
  formData,
  updateFormData,
  validationErrors,
}) {

  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-1 text-lg font-semibold text-gray-900">
          Terms and Conditions
        </h2>
        <p className="text-sm text-gray-600">
          Define the terms and conditions that govern this policy
        </p>
      </div>

      <div className="mb-6 space-y-4">
        <div>
          <label className="block mb-2 text-sm font-semibold text-gray-700">
            Terms and Conditions <span className="text-red-500">*</span>
          </label>
          <p className="mb-3 text-xs text-gray-500">
            Choose how you want to provide your terms and conditions
          </p>
          
          {/* Input Type Selection */}
          <div className="flex flex-wrap gap-3 mb-4">
            <button
              type="button"
              onClick={() => updateFormData("termsInputType", "text")}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                formData.termsInputType === "text"
                  ? "bg-blue-600 text-white shadow-md"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Text Area
            </button>
            <button
              type="button"
              onClick={() => updateFormData("termsInputType", "url")}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                formData.termsInputType === "url"
                  ? "bg-blue-600 text-white shadow-md"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              URL Link
            </button>
          </div>

          {/* Text Area Input */}
          {formData.termsInputType === "text" && (
            <div className="space-y-2">
              <textarea
                value={formData.termsAndConditions || ""}
                onChange={(e) =>
                  updateFormData("termsAndConditions", e.target.value)
                }
                placeholder="Enter the terms and conditions for this policy..."
                rows={8}
                className="px-4 py-3 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500">
                Provide detailed terms and conditions that govern this policy
              </p>
            </div>
          )}

          {/* URL Input */}
          {formData.termsInputType === "url" && (
            <div className="space-y-2">
              <input
                type="url"
                value={formData.termsAndConditions || ""}
                onChange={(e) =>
                  updateFormData("termsAndConditions", e.target.value)
                }
                placeholder="https://example.com/terms-and-conditions"
                className="px-4 py-3 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500">
                Enter the URL where your terms and conditions are hosted
              </p>
            </div>
          )}
        </div>

        {validationErrors.termsAndConditions && (
          <p className="flex items-center mt-2 text-sm text-red-600">
            <AlertCircle className="mr-1 w-4 h-4" />
            {validationErrors.termsAndConditions}
          </p>
        )}
      </div>
    </div>
  );
}

// =========================
// STEP 6: Review
// =========================
export function StepReview({ formData, policyTypes }) {
  const selectedPolicyType = policyTypes.find(
    (type) => type.id === formData.policyType
  );
  const isTripSecure = formData.insuranceType === "tripsecure";
  const isTravel = formData.insuranceType === "travel";
  const isLife = formData.insuranceType === "life";
  const isGeneral = formData.insuranceType === "general";
  const isIntercity = formData.policyType === "intercity_travel";
  const isMotoRider = formData.policyType === "okada_rider";
  const isDailyCommuter = formData.policyType === "daily_commuter";
  const isFixedCommuter = isMotoRider || isDailyCommuter;

  const getInsuranceTypeName = () => {
    if (isTripSecure) return "TripSecure Insurance";
    if (isTravel) return "Travel Insurance";
    if (isLife) return "Life Insurance";
    if (isGeneral) return "General Insurance";
    return "Insurance";
  };

  const getSubcategoryName = () => {
    const subcategories = {
      health_insurance: "Health Insurance",
      home_insurance: "Home Insurance",
      accident_insurance: "Accident Insurance",
      fire_insurance: "Fire Insurance",
      marine_insurance: "Marine Insurance",
      liability_insurance: "Liability Insurance",
    };
    return subcategories[formData.subcategory] || formData.subcategory;
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      {/* Policy Preview Card */}
      <div className="overflow-hidden relative p-4 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-2xl border border-blue-200 shadow-lg sm:p-6 lg:p-8">
        <div className="flex flex-col mb-4 space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0 sm:mb-6">
          <h3 className="flex items-center text-lg font-semibold text-gray-900 sm:text-xl">
            <div className="flex-shrink-0 p-2 mr-3 bg-blue-100 rounded-lg">
              <Eye className="w-5 h-5 text-blue-600 sm:w-6 sm:h-6" />
            </div>
            <span className="break-words">Policy Review</span>
          </h3>
          <div className="flex items-center self-start px-3 py-1 bg-yellow-100 rounded-full sm:self-auto">
            <div className="mr-2 w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
            <span className="text-xs font-medium text-yellow-700 sm:text-sm">
              Ready to Submit
            </span>
          </div>
        </div>

        {/* Policy Summary */}
        <div className="p-4 bg-white rounded-xl shadow-md sm:p-6">
          <h4 className="mb-2 text-lg font-bold text-gray-900 break-words sm:text-xl">
            {formData.policyName || "TripSecure Road Trip Insurance"}
          </h4>
          <div className="flex flex-col mb-4 space-y-3 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
            <div className="flex items-center space-x-3">
              {(() => {
                const Icon = isTripSecure
                  ? selectedPolicyType?.icon || Car
                  : isTravel
                  ? Plane
                  : isLife
                  ? Shield
                  : Home;
                return <Icon className="flex-shrink-0 w-7 h-7 text-gray-700 sm:w-8 sm:h-8" />;
              })()}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 break-words">
                  {isTripSecure
                    ? selectedPolicyType?.name || "Policy Type"
                    : isTravel
                    ? getInsuranceTypeName()
                    : getInsuranceTypeName()}
                </p>
                {isGeneral && formData.subcategory && (
                  <p className="text-sm text-gray-600 break-words">
                    Subcategory: {getSubcategoryName()}
                  </p>
                )}
                {isTravel && (
                  <p className="text-sm text-gray-600 break-words">
                    {formData.shortDescription ||
                      "Protect your journey with comprehensive coverage"}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Intercity Details */}
          {isIntercity && (
            <div className="mt-4 space-y-3">
              <div className="flex flex-col py-2 space-y-1 border-t border-gray-200 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
                <span className="font-medium text-gray-600">
                  Coverage Type:
                </span>
                <span className="font-semibold text-gray-900 capitalize">
                  {formData.geography}
                </span>
              </div>

              {Object.keys(formData.regionalRoutes || {}).some(
                (k) => formData.regionalRoutes[k].selected
              ) && (
                <div className="mt-2">
                  <h5 className="mb-2 text-sm font-semibold text-gray-700">
                    Regional Routes:
                  </h5>
                  <div className="space-y-1">
                    {Object.entries(formData.regionalRoutes || {})
                      .filter(([_, data]) => data.selected)
                      .map(([destination, data]) => (
                        <div
                          key={destination}
                          className="flex flex-col px-3 py-2 space-y-1 text-sm bg-blue-50 rounded sm:flex-row sm:justify-between sm:space-y-0"
                        >
                          <span className="break-words">{destination}</span>
                          <span className="font-semibold text-blue-600">
                            {formatMoney(data.price)}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {Object.keys(formData.internationalRoutes || {}).some(
                (k) => formData.internationalRoutes[k].selected
              ) && (
                <div className="mt-2">
                  <h5 className="mb-2 text-sm font-semibold text-gray-700">
                    International Routes:
                  </h5>
                  <div className="space-y-1">
                    {Object.entries(formData.internationalRoutes || {})
                      .filter(([_, data]) => data.selected)
                      .map(([destination, data]) => (
                        <div
                          key={destination}
                          className="flex flex-col px-3 py-2 space-y-1 text-sm bg-indigo-50 rounded sm:flex-row sm:justify-between sm:space-y-0"
                        >
                          <span className="break-words">{destination}</span>
                          <span className="font-semibold text-indigo-600">
                            {formatMoney(data.price)}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Motor Rider / Daily Commuter Details */}
          {isFixedCommuter && (
            <div className={`p-4 mt-4 rounded-lg ${isDailyCommuter ? "bg-blue-50" : "bg-orange-50"}`}>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {FIXED_COMMUTER_COVERAGES.map((tier) => (
                  <div key={tier.key} className="flex justify-between items-center">
                    <div>
                      <span className="text-sm text-gray-600">Duration</span>
                      <p className="font-bold text-gray-900">{tier.label}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-sm text-gray-600">Your price</span>
                      <p className={`text-lg font-bold ${isDailyCommuter ? "text-blue-600" : "text-orange-600"}`}>
                        {formatMoney(formData[tier.key] || 0)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Packages for Life, General, and Travel Insurance */}
          {(isLife || isGeneral || isTravel) &&
            formData.packages &&
            formData.packages.length > 0 && (
              <div className="pt-4 mt-4 border-t border-gray-200">
                <h5 className="mb-3 font-semibold text-gray-800">Packages:</h5>
                <div className="space-y-3">
                  {formData.packages.map((pkg, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border ${
                        isLife
                          ? "bg-green-50 border-green-200"
                          : isGeneral
                          ? "bg-purple-50 border-purple-200"
                          : "bg-indigo-50 border-indigo-200"
                      }`}
                    >
                      <div className="flex gap-3 justify-between items-start mb-2">
                        <h6 className="flex-1 min-w-0 font-semibold text-gray-900 break-words">
                          {pkg.productName || `Package ${index + 1}`}
                        </h6>
                        <span
                          className={`font-bold flex-shrink-0 ${
                            pkg.is_quote_only
                              ? "text-amber-600"
                              : isLife
                              ? "text-green-600"
                              : isGeneral
                              ? "text-purple-600"
                              : "text-indigo-600"
                          }`}
                        >
                          {pkg.is_quote_only ? "Price on quote" : `₵${pkg.premium || "0"}`}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 gap-2 mt-2 text-sm sm:grid-cols-2">
                        <div>
                          <span className="text-gray-600">Max Claims: </span>
                          <span className="font-medium text-gray-900">
                            {pkg.max_claims != null ? pkg.max_claims : "3"}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Contribution Type: </span>
                          <span className="font-medium text-gray-900 capitalize">
                            {pkg.premiumDuration || "monthly"}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Package Duration: </span>
                          <span className="font-medium text-gray-900">
                            {pkg.durationValue && pkg.durationUnit
                              ? (() => {
                                  const numValue = parseInt(pkg.durationValue) || 0;
                                  let unit = pkg.durationUnit;
                                  // Handle new standardized duration units
                                  if (unit === "hours" || unit === "hour") {
                                    unit = numValue === 1 ? "hour" : "hours";
                                  } else if (unit === "days" || unit === "day") {
                                    unit = numValue === 1 ? "day" : "days";
                                  } else if (unit === "weeks" || unit === "week") {
                                    unit = numValue === 1 ? "week" : "weeks";
                                  } else if (unit === "months" || unit === "month") {
                                    unit = numValue === 1 ? "month" : "months";
                                  } else if (unit === "years") {
                                    unit = numValue === 1 ? "year" : "years";
                                  } else {
                                    // Legacy support for old units
                                    if (unit === "monthly") {
                                      unit = numValue === 1 ? "month" : "months";
                                    } else if (unit === "weekly") {
                                      unit = numValue === 1 ? "week" : "weeks";
                                    } else if (unit === "yearly") {
                                      unit = numValue === 1 ? "year" : "years";
                                    }
                                  }
                                  return `${numValue} ${unit}`;
                                })()
                              : "N/A"}
                          </span>
                        </div>
                      </div>
                      {pkg.description && (
                        <p className="mt-2 text-sm text-gray-700">
                          {pkg.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Benefits Summary */}
          {formData.insuranceCoverageList &&
            formData.insuranceCoverageList.length > 0 && (
              <div className="pt-4 mt-4 border-t border-gray-200">
                <h5 className="mb-2 font-semibold text-gray-800">
                  Coverage Benefits:
                </h5>
                <div className="space-y-1">
                  {formData.insuranceCoverageList.map((benefit, index) => (
                    <div
                      key={index}
                      className="flex items-start text-sm text-gray-700"
                    >
                      <CheckCircle className="flex-shrink-0 mr-2 w-4 h-4 text-green-500 mt-0.5" />
                      <span className="break-words">
                        {benefit.title || `Benefit ${index + 1}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Exclusions Summary */}
          {formData.exclusionsList && formData.exclusionsList.length > 0 && (
            <div className="pt-4 mt-4 border-t border-gray-200">
              <h5 className="mb-2 font-semibold text-gray-800">Exclusions:</h5>
              <div className="space-y-1">
                {formData.exclusionsList.map((exclusion, index) => (
                  <div
                    key={index}
                    className="flex items-start text-sm text-gray-700"
                  >
                    <X className="flex-shrink-0 mr-2 w-4 h-4 text-red-500 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <span className="break-words">
                        {exclusion.text || `Exclusion ${index + 1}`}
                      </span>
                      {exclusion.type && exclusion.type !== "general" && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                          {exclusion.type.toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Provider-managed fields for Travel, Life, and General Insurance */}
          {(isTravel || isLife || isGeneral) && (
            <>
              {/* Eligibility Criteria Summary */}
              {formData.eligibilityCriteria &&
                formData.eligibilityCriteria.length > 0 && (
                  <div className="pt-4 mt-4 border-t border-gray-200">
                    <h5 className="mb-2 font-semibold text-gray-800">
                      Eligibility Criteria:
                    </h5>
                    <div className="space-y-1">
                      {formData.eligibilityCriteria
                        .filter((c) => c && c.trim() !== "")
                        .map((criterion, index) => (
                          <div
                            key={index}
                            className="flex items-start text-sm text-gray-700"
                          >
                            <CheckCircle className="flex-shrink-0 mr-2 w-4 h-4 text-green-500 mt-0.5" />
                            <span className="break-words">{criterion}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

              {/* Terms and Conditions Summary */}
              {formData.termsAndConditions && (
                <div className="pt-4 mt-4 border-t border-gray-200">
                  <h5 className="mb-2 font-semibold text-gray-800">
                    Terms and Conditions:
                  </h5>
                  {formData.termsInputType === "url" ? (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-700">
                          URL:
                        </span>
                        <a
                          href={formData.termsAndConditions}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 underline break-all hover:text-blue-800"
                        >
                          {formData.termsAndConditions}
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="p-3 text-sm text-gray-700 whitespace-pre-wrap break-words bg-gray-50 rounded-lg">
                      {formData.termsAndConditions}
                    </div>
                  )}
                </div>
              )}

              {/* Claim Process Steps Summary */}
              {formData.claimProcessSteps &&
                formData.claimProcessSteps.length > 0 && (
                  <div className="pt-4 mt-4 border-t border-gray-200">
                    <h5 className="mb-2 font-semibold text-gray-800">
                      Claim Process Steps:
                    </h5>
                    <div className="space-y-3">
                      {formData.claimProcessSteps
                        .filter(
                          (step) =>
                            step.title?.trim() &&
                            step.description?.trim() &&
                            step.timeframe?.trim()
                        )
                        .map((step, index) => (
                          <div
                            key={index}
                            className="p-3 bg-blue-50 rounded-lg border border-blue-200"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center space-x-2">
                                <span className="flex justify-center items-center w-6 h-6 text-xs font-bold text-white bg-blue-600 rounded-full">
                                  {step.step_number || index + 1}
                                </span>
                                <h6 className="font-semibold text-gray-900">
                                  {step.title}
                                </h6>
                              </div>
                              {step.timeframe && (
                                <span className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                                  {step.timeframe}
                                </span>
                              )}
                            </div>
                            <p className="mb-2 text-sm text-gray-700">
                              {step.description}
                            </p>
                            {step.requirements && (
                              <div className="pt-2 mt-2 border-t border-blue-200">
                                <span className="text-xs font-medium text-gray-600">
                                  Requirements:{" "}
                                </span>
                                <span className="text-xs text-gray-700">
                                  {step.requirements}
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
            </>
          )}
        </div>
      </div>

      {/* Important Note */}
      <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h4 className="mb-1 font-semibold text-yellow-900">
              Before You Submit
            </h4>
            <ul className="space-y-1 text-sm list-disc list-inside text-yellow-700">
              {isTripSecure ? (
                <>
                  <li className="break-words">
                    Policy name is fixed as "TripSecure Road Trip Insurance"
                  </li>
                  <li className="break-words">
                    Eligibility criteria and Terms & Conditions are managed by
                    TripSecure
                  </li>
                  <li className="break-words">
                    Risk assessment is handled automatically
                  </li>
                  <li className="break-words">
                    You cannot set prices higher than the maximum allowed
                  </li>
                </>
              ) : isTravel ? (
                <>
                  <li className="break-words">
                    Policy name is automatically generated as: [Company Name]
                    Travel Insurance
                  </li>
                  <li className="break-words">
                    You can define and manage Eligibility Criteria, Terms &
                    Conditions, and Claim Process Steps for your policy
                  </li>
                  <li className="break-words">
                    Risk assessment is handled automatically
                  </li>
                  <li className="break-words">
                    Ensure all package details are complete and accurate
                  </li>
                </>
              ) : isLife ? (
                <>
                  <li className="break-words">
                    Policy name is automatically generated as: [Company Name]
                    Life Insurance
                  </li>
                  <li className="break-words">
                    You can define and manage Eligibility Criteria, Terms &
                    Conditions, and Claim Process Steps for your policy
                  </li>
                  <li className="break-words">
                    Risk assessment is handled automatically
                  </li>
                  <li className="break-words">
                    Ensure all package details are complete and accurate
                  </li>
                </>
              ) : (
                <>
                  <li className="break-words">
                    Policy name is automatically generated as: [Company Name]
                    [Subcategory]
                  </li>
                  <li className="break-words">
                    You can define and manage Eligibility Criteria, Terms &
                    Conditions, and Claim Process Steps for your policy
                  </li>
                  <li className="break-words">
                    Risk assessment is handled automatically
                  </li>
                  <li className="break-words">
                    Ensure all package details are complete and accurate
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
